import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  BookOpen,
  CheckCircle,
  Download,
  FileText,
  Flag,
  FolderOpen,
  Image as ImageIcon,
  Loader2,
  PenLine,
  Plus,
  Send,
  ShieldCheck,
  Sparkles,
  Trash2,
  UploadCloud,
  UserRound,
} from 'lucide-react';
import { antiBrushHeaders } from '../utils/analytics';
import { formatBeijingRelativeTime, parseServerDateTime } from '../utils/dateTime';
import { compactDefaultPublishDescription } from '../utils/publishDescription';
import { humanizeInspirationStream } from '../utils/inspirationStreamPreview';
import AppendChaptersButton from '../components/AppendChaptersButton';

type StageKey = 'inspiration' | 'settings' | 'characters' | 'outline' | 'chapters' | 'review' | 'publish';
const apiBaseUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:65181'}${process.env.REACT_APP_API_VERSION === undefined ? '/api/v1' : process.env.REACT_APP_API_VERSION}`;
const SYNOPSIS_LIMIT = 1500;

interface StagePanelProps {
  activeTab: StageKey;
  projectId?: number;
  activeInspirationId?: number | null;
  inspirationItems?: BackendInspiration[];
  generationSettings?: GenerationSettings | null;
  workbenchChapters?: Array<{
    backendId?: number;
    index: string;
    title: string;
    summary: string;
    content: string;
    words: number;
    targetWords: number;
    score?: number | null;
    review?: ReviewPayload | null;
  }>;
  stageCompletion: Record<StageKey, boolean>;
  score: number;
  setToast: (message: string) => void;
  onInspirationTitleChange?: (title: string) => void;
  onOpenChapters: () => void;
  onOpenCharacters: () => void;
  onOpenOutline: () => void;
  onOpenSettings: () => void;
  onOpenPublish: () => void;
  onStageCompletionChange: (tab: StageKey, done: boolean) => void;
  onWorkbenchRefresh?: (inspirationId?: number | null) => Promise<unknown> | void;
  onInspirationSelect?: (inspirationId: number, title?: string) => void;
  onEnsureWorkbenchReady?: () => Promise<number>;
  onAssetsRefresh?: () => Promise<void> | void;
  onRecalcScore: () => void;
  onRequireLogin: () => boolean;
  onGenerationFailure?: (message: string) => void;
}

interface SynopsisDraft {
  id: string;
  backendId?: number;
  seed: string;
  title: string;
  premise: string;
  stakes: string;
  conflict: string;
  hook: string;
  boundary: string;
  status: string;
  updatedAt: string;
  sortTime: number;
}

interface BackendInspiration {
  id: number;
  seed?: string;
  title?: string;
  premise?: string;
  stakes?: string;
  conflict?: string;
  hook?: string;
  boundary?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

interface BackendSetting {
  id: number;
  setting_key: string;
  setting_value: string;
  note: string;
}

interface LibraryCategoryOption {
  id: string;
  sort: number;
  channel: '男频' | '女频';
  name: string;
}

interface BackendProject {
  id: number;
  title: string;
  description?: string;
  platform: string;
  genre: string;
  chapter_target: number;
  words_per_chapter: number;
  publish?: {
    slug?: string;
    title?: string;
    author?: string;
    description?: string;
    cover_url?: string;
    status?: string;
    book_status?: '连载中' | '已完结';
    channel?: '男频' | '女频';
    category?: string;
    published_at?: string;
    source_inspiration_id?: number;
    updated_at?: string;
  };
  settings?: {
    initial_chapter_count?: number;
    _workflow?: {
      source_inspiration_id?: number;
    };
    [key: string]: unknown;
  };
}

interface BackendCharacter {
  id: number;
  name: string;
  role?: string;
  faction?: string;
  age?: string;
  identity?: string;
  goal?: string;
  fear?: string;
  personality?: string;
  speech?: string;
  secret?: string;
  requirement?: string;
  arc?: string;
  boundary?: string;
  status?: string;
  tags?: string[];
}

interface BackendRelation {
  id: number;
  source_character_id: number;
  target_character_id: number;
  relation_type: string;
  description?: string;
}

interface BackendOutline {
  id: number;
  outline_type: string;
  chapter_no?: number | null;
  title?: string;
  volume?: string;
  goal?: string;
  conflict?: string;
  must_cover?: string[];
  must_not?: string[];
  characters?: string[];
  hooks_to_advance?: string[];
  expected_ending?: string;
  summary?: string;
}

interface BackendHook {
  id: number;
  title: string;
  description?: string;
  status: string;
  planted_chapter?: number | null;
  expected_payoff_chapter?: number | null;
}

type RecoverableGenerationStage = 'settings' | 'characters' | 'outline';

interface BackendAITask {
  id: number;
  task_type: string;
  status: string;
  error?: string;
  created_at?: string;
  updated_at?: string;
}

interface RecoverableGenerationState {
  projectId: number;
  inspirationId: number;
  stage: RecoverableGenerationStage;
  status: 'running' | 'done' | 'failed';
  message: string;
  updatedAt: number;
}

const RECOVERABLE_GENERATION_KEY = 'lingbi-recoverable-stage-generation';
const RECOVERABLE_TASK_TYPES: Record<RecoverableGenerationStage, string> = {
  settings: 'setting_designer',
  characters: 'character_designer',
  outline: 'outline_designer',
};
const RECOVERABLE_STAGE_LABELS: Record<RecoverableGenerationStage, string> = {
  settings: '设定',
  characters: '人物设定',
  outline: '大纲',
};

const readRecoverableGeneration = (): RecoverableGenerationState | null => {
  try {
    const value = JSON.parse(localStorage.getItem(RECOVERABLE_GENERATION_KEY) || 'null');
    if (!value || !['settings', 'characters', 'outline'].includes(value.stage)) return null;
    return value as RecoverableGenerationState;
  } catch {
    return null;
  }
};

const saveRecoverableGeneration = (state: RecoverableGenerationState) => {
  localStorage.setItem(RECOVERABLE_GENERATION_KEY, JSON.stringify(state));
};

const clearRecoverableGeneration = () => {
  localStorage.removeItem(RECOVERABLE_GENERATION_KEY);
};

interface BackendChapterSnapshot {
  id: number;
  chapter_no?: number;
  title?: string;
  summary?: string;
  content?: string;
  words?: number;
  score?: number | null;
  review?: ReviewPayload | null;
}

interface WorkbenchSnapshot {
  project: BackendProject;
  inspirations: BackendInspiration[];
  settings: BackendSetting[];
  characters: BackendCharacter[];
  relations: BackendRelation[];
  outlines: BackendOutline[];
  chapters?: BackendChapterSnapshot[];
  hooks: BackendHook[];
  ai_tasks?: BackendAITask[];
}

interface WorkflowStageStatus {
  done: boolean;
  locked: boolean;
  count: number;
}

interface WorkflowStatusResponse {
  project_id: number;
  requested_inspiration_id?: number | null;
  active_inspiration_id?: number | null;
  stages: Record<StageKey, WorkflowStageStatus>;
}

interface GeneratedSettings {
  genre: string;
  style_platform: string;
  style_name: string;
  style_note: string;
  narrative_perspective: string;
  rhythms: string[];
  chapter_target: number;
  initial_chapter_count: number;
  words_per_chapter: number;
  chapter_word_limit: number;
  total_words?: number;
  boundary: string;
}

interface GenerationSettings {
  max_chapters: number;
  default_chapters: number;
  default_words_per_chapter: number;
  publish_min_score: number;
}

interface ReviewPayload {
  score?: number;
  dimensions?: Array<{ name?: string; score?: number; passed?: boolean; status?: string; comment?: string; suggestion?: string }>;
  problems?: Array<{ level?: string; message?: string; location?: string; suggestion?: string }>;
  suggestions?: string[];
  risk_flags?: string[];
  continuity_notes?: string[];
}

interface ReviewResult {
  chapter: BackendChapterSnapshot;
  review: ReviewPayload;
}

interface RepairReviewResult {
  applied: boolean;
  message: string;
  chapter?: BackendChapterSnapshot | null;
  review?: ReviewPayload | null;
}

interface PublishResult {
  message: string;
  book: {
    slug: string;
    title: string;
  };
  project: BackendProject;
}

interface PublishCoverResult {
  cover_url: string;
  prompt: string;
  model?: string;
  provider?: string;
}

const waitForCoverImage = (source: string, timeoutMs = 20000) => new Promise<void>((resolve, reject) => {
  const image = new Image();
  const timer = window.setTimeout(() => {
    image.src = '';
    reject(new Error('封面已经生成，但图片加载校验超时，请稍后刷新重试'));
  }, timeoutMs);
  image.onload = () => {
    window.clearTimeout(timer);
    resolve();
  };
  image.onerror = () => {
    window.clearTimeout(timer);
    reject(new Error('封面已经生成，但图片资源暂时无法显示，请重新生成或稍后重试'));
  };
  image.src = source;
});

type CharacterRelationTuple = [string, string, string, string];

const authHeaders = () => {
  const token = localStorage.getItem('lingbi-token');
  return {
    'Content-Type': 'application/json',
    ...antiBrushHeaders(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const apiRequest = async <T,>(path: string, options: RequestInit = {}): Promise<T> => {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    headers: {
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  const text = await response.text();
  let data: any = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!response.ok) {
    const gatewayMessage = response.status === 502 || response.status === 504
      ? 'AI生成等待超时，系统没有丢失已完成内容，请稍后查看或重试'
      : `请求失败（${response.status}）`;
    throw new Error(formatApiError(data) || gatewayMessage);
  }
  if (text && data === null) throw new Error('服务返回格式异常，请稍后重试');
  return data as T;
};

const formatApiError = (data: any) => {
  const detail = data?.detail || data?.message;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const field = Array.isArray(item?.loc) ? item.loc.filter((part: unknown) => part !== 'body').join('.') : '';
        return [field, item?.msg].filter(Boolean).join('：');
      })
      .filter(Boolean)
      .join('；');
  }
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return '';
};

const workbenchPath = (projectId: number, inspirationId?: number, includeChapterContent = false) => {
  const params = new URLSearchParams();
  if (inspirationId) params.set('inspiration_id', String(inspirationId));
  if (!includeChapterContent) params.set('include_chapter_content', 'false');
  const query = params.toString();
  return `/novel_project_app/projects/${projectId}/workbench${query ? `?${query}` : ''}`;
};

const clampNumber = (value: unknown, fallback: number, min: number, max: number) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(min, Math.min(max, Math.round(number)));
};

const streamErrorMessage = async (response: Response, fallback: string) => {
  try {
    const text = await response.text();
    if (!text) return fallback;
    const data = JSON.parse(text);
    return formatApiError(data) || text || fallback;
  } catch {
    return fallback;
  }
};

const parseStreamEvent = (line: string) => {
  try {
    return JSON.parse(line);
  } catch {
    throw new Error('生成服务返回异常，请重试');
  }
};

const friendlyGenerationError = (error: unknown, fallback: string) => {
  const message = error instanceof Error ? error.message : fallback;
  if (
    /Unexpected token|DOCTYPE|not valid JSON|JSON at position|Traceback|Internal Server Error|status code/i.test(message)
  ) {
    return '生成失败，请重试';
  }
  return message || fallback;
};

interface ZipFileEntry {
  name: string;
  data: string | Uint8Array;
  mime?: string;
}

const textEncoder = new TextEncoder();
const crcTable = Array.from({ length: 256 }, (_, index) => {
  let c = index;
  for (let k = 0; k < 8; k += 1) c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
  return c >>> 0;
});

const sanitizeFileName = (value: string, fallback = 'novel') => (
  (value || fallback)
    .replace(/[《》]/g, '')
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)
  || fallback
);

const normalizeText = (value?: string | null) => String(value || '').replace(/\r\n/g, '\n').trim();

const crc32 = (bytes: Uint8Array) => {
  let crc = 0xffffffff;
  bytes.forEach((byte) => {
    crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  });
  return (crc ^ 0xffffffff) >>> 0;
};

const dosDateTime = (date = new Date()) => {
  const time = ((date.getHours() & 0x1f) << 11) | ((date.getMinutes() & 0x3f) << 5) | ((Math.floor(date.getSeconds() / 2)) & 0x1f);
  const dosDate = (((date.getFullYear() - 1980) & 0x7f) << 9) | (((date.getMonth() + 1) & 0xf) << 5) | (date.getDate() & 0x1f);
  return { time, date: dosDate };
};

const writeUint16 = (view: DataView, offset: number, value: number) => view.setUint16(offset, value, true);
const writeUint32 = (view: DataView, offset: number, value: number) => view.setUint32(offset, value >>> 0, true);

const createZipBlob = (files: ZipFileEntry[]) => {
  const now = dosDateTime();
  let offset = 0;
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];

  files.forEach((file) => {
    const nameBytes = textEncoder.encode(file.name);
    const dataBytes = typeof file.data === 'string' ? textEncoder.encode(file.data) : file.data;
    const checksum = crc32(dataBytes);

    const local = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(local.buffer);
    writeUint32(localView, 0, 0x04034b50);
    writeUint16(localView, 4, 20);
    writeUint16(localView, 6, 0x0800);
    writeUint16(localView, 8, 0);
    writeUint16(localView, 10, now.time);
    writeUint16(localView, 12, now.date);
    writeUint32(localView, 14, checksum);
    writeUint32(localView, 18, dataBytes.length);
    writeUint32(localView, 22, dataBytes.length);
    writeUint16(localView, 26, nameBytes.length);
    local.set(nameBytes, 30);
    localParts.push(local, dataBytes);

    const central = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(central.buffer);
    writeUint32(centralView, 0, 0x02014b50);
    writeUint16(centralView, 4, 20);
    writeUint16(centralView, 6, 20);
    writeUint16(centralView, 8, 0x0800);
    writeUint16(centralView, 10, 0);
    writeUint16(centralView, 12, now.time);
    writeUint16(centralView, 14, now.date);
    writeUint32(centralView, 16, checksum);
    writeUint32(centralView, 20, dataBytes.length);
    writeUint32(centralView, 24, dataBytes.length);
    writeUint16(centralView, 28, nameBytes.length);
    writeUint32(centralView, 42, offset);
    central.set(nameBytes, 46);
    centralParts.push(central);

    offset += local.length + dataBytes.length;
  });

  const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  writeUint32(endView, 0, 0x06054b50);
  writeUint16(endView, 8, files.length);
  writeUint16(endView, 10, files.length);
  writeUint32(endView, 12, centralSize);
  writeUint32(endView, 16, offset);

  return new Blob([...localParts, ...centralParts, end], { type: 'application/zip' });
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const parseInspirationTime = (item: Pick<BackendInspiration, 'created_at' | 'updated_at'>) => {
  return parseServerDateTime(item.created_at);
};

const formatInspirationTime = (item: Pick<BackendInspiration, 'created_at' | 'updated_at'>) => {
  return formatBeijingRelativeTime(parseInspirationTime(item));
};

const sortDrafts = (items: SynopsisDraft[]) => (
  [...items].sort((a, b) => (b.sortTime || 0) - (a.sortTime || 0))
);

const toDraft = (item: BackendInspiration): SynopsisDraft => ({
  id: `idea-${item.id}`,
  backendId: item.id,
  seed: item.seed || '',
  title: item.title || '《未命名灵感》',
  premise: item.premise || '',
  stakes: item.stakes || '',
  conflict: item.conflict || '',
  hook: item.hook || '',
  boundary: item.boundary || '',
  status: item.status || '灵感',
  updatedAt: formatInspirationTime(item),
  sortTime: parseInspirationTime(item),
});

const hasDraftContent = (draft: SynopsisDraft) => {
  const text = [
    draft.seed,
    draft.premise,
    draft.stakes,
    draft.conflict,
    draft.hook,
    draft.boundary,
  ].join('').trim();
  const customTitle = draft.title.replace(/[《》]/g, '').trim();
  return Boolean(text || (customTitle && !['未命名新书', '未命名灵感'].includes(customTitle)));
};

interface CharacterProfile {
  id: string;
  name: string;
  role: string;
  faction: '主角' | '亲属' | '盟友' | '师长' | '谜团' | '敌对';
  age: string;
  identity: string;
  goal: string;
  personality: string;
  speech: string;
  secret: string;
  requirement: string;
  arc: string;
  x: number;
  y: number;
}

const characterPositions = [
  [30, 50],
  [13, 35],
  [10, 22],
  [52, 24],
  [72, 50],
  [90, 26],
  [10, 62],
  [22, 76],
  [52, 78],
  [30, 26],
  [70, 84],
  [92, 64],
  [45, 48],
  [63, 34],
  [78, 72],
  [18, 48],
] as const;

const normalizeFaction = (value?: string): CharacterProfile['faction'] => {
  const options: CharacterProfile['faction'][] = ['主角', '亲属', '盟友', '师长', '谜团', '敌对'];
  return options.includes(value as CharacterProfile['faction']) ? value as CharacterProfile['faction'] : '谜团';
};

const emptyCharacterProfile: CharacterProfile = {
  id: 'empty-character',
  name: '生成中',
  role: '',
  faction: '谜团',
  age: '',
  identity: '',
  goal: '',
  personality: '',
  speech: '',
  secret: '',
  requirement: '',
  arc: '',
  x: 50,
  y: 50,
};

const toCharacterProfile = (item: BackendCharacter, index: number): CharacterProfile => {
  const position = characterPositions[index % characterPositions.length];
  return {
    id: `char-${item.id}`,
    name: item.name || '未命名人物',
    role: item.role || item.identity || '关键人物',
    faction: normalizeFaction(item.faction),
    age: item.age || '未知',
    identity: item.identity || item.role || '',
    goal: item.goal || '',
    personality: item.personality || '',
    speech: item.speech || '',
    secret: item.secret || '',
    requirement: item.requirement || item.boundary || '',
    arc: item.arc || '',
    x: position[0],
    y: position[1],
  };
};

const relationVisualType = (relationType: string) => {
  if (/敌|对立|追索|威胁|反派/.test(relationType)) return 'enemy';
  if (/师|教|引导/.test(relationType)) return 'mentor';
  if (/友|盟|同伴|帮助/.test(relationType)) return 'friend';
  if (/亲|父|母|祖|兄|姐|血缘/.test(relationType)) return 'kin';
  return 'mystery';
};

const compactRelationLabel = (label: string) => {
  const value = label.trim();
  return value.length > 8 ? `${value.slice(0, 8)}…` : value;
};

const toCharacterRelations = (
  relations: BackendRelation[] = [],
  profiles: CharacterProfile[],
): CharacterRelationTuple[] => {
  const profileIds = new Set(profiles.map((item) => item.id));
  return relations
    .map((item) => {
      const from = `char-${item.source_character_id}`;
      const to = `char-${item.target_character_id}`;
      if (!profileIds.has(from) || !profileIds.has(to)) return null;
      return [from, to, item.relation_type || '关联', relationVisualType(item.relation_type || '')] as CharacterRelationTuple;
    })
    .filter(Boolean) as CharacterRelationTuple[];
};

interface OutlineChapter {
  id: string;
  no: number;
  title: string;
  purpose: string;
  summary: string;
  hook: string;
  foreshadow: string;
}

const emptyOutlineChapter: OutlineChapter = {
  id: 'outline-empty',
  no: 1,
  title: '生成中',
  purpose: '',
  summary: '',
  hook: '',
  foreshadow: '',
};

interface ForeshadowPlan {
  id: string;
  name: string;
  plantChapter: number;
  revealChapter: number;
  state: string;
  rule: string;
}

const genericChapterTitleWords = new Set(['开局', '开始', '铺垫', '发展', '推进', '转折', '高潮', '结尾', '终局', '尾声', '收束', '回收']);
const fallbackChapterTitleCores = ['命运推门', '第一道规则', '无法回头的选择', '代价显形', '陌生盟友', '秘密松动', '旧线反咬', '敌意逼近', '规则裂缝', '真相试探', '关系反转', '代价加深', '伏笔回响', '旧账浮出', '最后筹码', '真相逼近', '选择落定'];

const stripChapterTitlePrefix = (value: string) => value
  .trim()
  .replace(/^第\s*[0-9零一二三四五六七八九十百千]+\s*[章节回]\s*[·:：、.\-\s]*/, '')
  .trim();

const isGenericChapterTitle = (value: string) => {
  const compact = stripChapterTitlePrefix(value).replace(/[\s·:：、.\-—_]+/g, '');
  if (!compact) return true;
  if (genericChapterTitleWords.has(compact)) return true;
  if (/^(被卷入主线事件|主角被卷入|主角尝试使用|尝试使用已有线索|旧线索和人物秘密|建立主角目标|回收核心伏笔|本章建立|本章推进)/.test(compact)) return true;
  return compact.length <= 4 && Array.from(genericChapterTitleWords).some((word) => compact.includes(word));
};

const cleanChapterTitleCore = (value: string) => stripChapterTitlePrefix(value)
  .split(/[。！？!?；;\n\r]/)[0]
  .replace(/^(本章|这一章|主角|他|她|他们|她们|少年|少女)/, '')
  .replace(/["“”'‘’《》#*`~]/g, '')
  .replace(/\s+/g, '')
  .replace(/^[，,、：:；;。.!！?？]+|[，,、：:；;。.!！?？]+$/g, '')
  .slice(0, 12);

const normalizeChapterTitle = (title: string, no: number, summary = '') => {
  const core = stripChapterTitlePrefix(title || '');
  if (core && !isGenericChapterTitle(core)) return core;
  const fromSummary = cleanChapterTitleCore(summary || '');
  if (fromSummary && !isGenericChapterTitle(fromSummary)) return fromSummary;
  return fallbackChapterTitleCores[(Math.max(no, 1) - 1) % fallbackChapterTitleCores.length];
};

const toOutlineChapter = (item: BackendOutline): OutlineChapter => {
  const no = Number(item.chapter_no || 1);
  return {
    id: `outline-${String(item.id || no).padStart(2, '0')}`,
    no,
    title: normalizeChapterTitle(item.title || '', no, item.summary || item.goal || ''),
    purpose: item.goal || '',
    summary: item.summary || '',
    hook: item.expected_ending || item.conflict || '',
    foreshadow: (item.hooks_to_advance || []).join('、'),
  };
};

const toForeshadowPlan = (item: BackendHook, index: number): ForeshadowPlan => ({
  id: `hook-${item.id || index + 1}`,
  name: item.title || `伏笔${index + 1}`,
  plantChapter: Number(item.planted_chapter || 1),
  revealChapter: Number(item.expected_payoff_chapter || item.planted_chapter || 1),
  state: item.status || '已规划',
  rule: item.description || '',
});

const genreOptions = ['都市', '玄幻', '悬疑', '科幻', '历史', '现实', '女频', '其他'];

const rhythmOptions = [
  ['短段高钩子', '段落短、句子直，每 600-900 字给一次信息钩子。'],
  ['强冲突推进', '每章必须有明确阻力、选择和后果。'],
  ['爽点密集', '能力、打脸、反转、收益以固定间隔出现。'],
  ['悬念递进', '每章回收一个小问题，同时抛出一个更大的问题。'],
  ['情绪拉扯', '亲情、爱情、友情或背叛线持续压迫主线选择。'],
  ['伏笔回收', '重要承诺必须记录预计回收章节，避免后期忘线。'],
];

const styleOptions = [
  ['番茄', '快节奏、短段落、强钩子，每章都要有冲突和追读点。'],
  ['起点', '重体系和成长线，适合升级、世界观、长期伏笔。'],
  ['晋江', '人物关系和情绪递进更细，角色弧光要稳定。'],
  ['七猫', '表达直给、冲突密集，移动端阅读节奏更快。'],
  ['通用', '不绑定平台，按题材和故事本身保持稳定文风。'],
];

const matchOption = (value: unknown, options: string[], fallback: string) => {
  const text = String(value || '').trim();
  if (options.includes(text)) return text;
  const matched = options.find((option) => option !== '其他' && text.includes(option));
  return matched || fallback;
};

const normalizeGenreSelection = (value: unknown) => matchOption(value, genreOptions, '其他');

const suggestedPublishCategory = (genre: unknown, channel: '男频' | '女频') => {
  const text = String(genre || '').trim();
  if (channel === '女频') {
    if (/悬疑|推理/.test(text)) return '悬疑推理';
    if (/科幻|末世|星际/.test(text)) return '科幻空间';
    if (/仙侠|修仙/.test(text)) return '仙侠奇缘';
    if (/玄幻|奇幻/.test(text)) return '玄幻言情';
    if (/历史|古代/.test(text)) return '古代言情';
    if (/校园|青春/.test(text)) return '青春校园';
    return '现代言情';
  }
  if (/悬疑|推理|灵异/.test(text)) return '悬疑脑洞';
  if (/科幻|末世|星际/.test(text)) return '科幻末世';
  if (/仙侠|修仙/.test(text)) return '东方仙侠';
  if (/修真/.test(text)) return '都市修真';
  if (/高武/.test(text)) return '都市高武';
  if (/历史|古代/.test(text)) return '历史古代';
  if (/赘婿|战神/.test(text)) return '战神赘婿';
  if (/玄幻/.test(text)) return '传统玄幻';
  if (/奇幻/.test(text)) return '西方奇幻';
  return '都市日常';
};

const normalizeStyleSelection = (value: unknown) => matchOption(value, styleOptions.map(([name]) => name), '通用');

const normalizeNarrativePerspective = (value: unknown) => {
  const raw = String(value || '').trim().replace(/[\s·_\-—（）()]+/g, '');
  if (!raw) return '';
  if (raw.includes('第一人称') || ['一人称', '主角自述', '自述视角'].includes(raw)) return '第一人称';
  if (raw.includes('第三人称')) {
    if (['全知', '上帝', '多视角'].some((marker) => raw.includes(marker))) return '第三人称全知';
    if (['限知', '有限', '单一', '单视角', '跟随'].some((marker) => raw.includes(marker))) return '第三人称限知';
  }
  return ['第一人称', '第三人称限知', '第三人称全知'].includes(raw) ? raw : '';
};

const normalizeRhythmSelection = (value: unknown) => {
  const rawItems = Array.isArray(value) ? value : String(value || '').split(/[、,，;\n]+/);
  const allowed = rhythmOptions.map(([name]) => name);
  const result: string[] = [];
  rawItems.forEach((item) => {
    const matched = matchOption(item, allowed, '');
    if (matched && !result.includes(matched)) result.push(matched);
  });
  return result.length ? result.slice(0, 5) : ['短段高钩子', '强冲突推进', '伏笔回收'];
};

const reviewDimensionDefaults = [
  ['一致性', '是否符合本章基准、设定边界、人物目标和前文事实。'],
  ['节奏', '章节推进是否紧凑，是否避免无效铺垫和说明过长。'],
  ['爽点钩子', '金手指价值、冲突收益、读者期待和章末钩子是否成立。'],
  ['伏笔人设', '伏笔推进或回收是否清楚，人物行为和状态是否稳定。'],
];

const normalizeReviewDimensions = (review?: ReviewPayload | null, fallbackScore = 88, passScore = 80) => {
  if (!review) {
    return reviewDimensionDefaults.map(([name, defaultComment]) => ({
      name,
      score: Number.NaN,
      passed: false,
      status: '待审',
      comment: defaultComment,
      suggestion: '完成审稿后显示该分项的真实判定。',
    }));
  }
  const raw = Array.isArray(review?.dimensions) ? review?.dimensions || [] : [];
  return reviewDimensionDefaults.map(([name, defaultComment], index) => {
    const matched = raw.find((item) => String(item.name || '').includes(name) || name.includes(String(item.name || '')));
    const defaultScore = fallbackScore >= passScore ? Math.min(96, passScore + 10 + index) : fallbackScore - 2 + index;
    const score = Math.max(0, Math.min(100, Number(matched?.score ?? defaultScore)));
    const passed = score >= passScore;
    return {
      name,
      score,
      passed: Boolean(passed),
      status: passed ? '通过' : '待修',
      comment: String(matched?.comment || defaultComment),
      suggestion: String(matched?.suggestion || ''),
    };
  });
};

const hasReviewChapterContent = (chapter?: BackendChapterSnapshot | null) => Boolean(String(chapter?.content || '').trim());

const hasStoredReview = (chapter?: BackendChapterSnapshot | null) => {
  const review = chapter?.review;
  return Boolean(
    chapter?.score != null
    || (review && Object.keys(review).length > 0)
    || review?.score != null
  );
};

const reviewScoreFromChapter = (chapter?: BackendChapterSnapshot | null) => {
  const raw = chapter?.score ?? chapter?.review?.score;
  const value = Number(raw);
  return Number.isFinite(value) ? Math.max(0, Math.min(100, Math.round(value))) : Number.NaN;
};

const isReviewStageComplete = (chapters: BackendChapterSnapshot[], publishMinScore: number) => {
  const contentChapters = chapters.filter(hasReviewChapterContent);
  return contentChapters.length > 0
    && contentChapters.every((chapter) => {
      const score = reviewScoreFromChapter(chapter);
      return Number.isFinite(score) && score >= publishMinScore;
    });
};

const reviewTargetSize = (mode: string) => mode === '单章精审' ? 1 : 3;

const reviewPayloadFromChapter = (chapter?: BackendChapterSnapshot | null): ReviewPayload | null => {
  if (!chapter || !hasStoredReview(chapter)) return null;
  return chapter.review || { score: reviewScoreFromChapter(chapter) };
};

const aggregateReviewPayload = (chapters: BackendChapterSnapshot[]): ReviewPayload | null => {
  if (!chapters.length || chapters.some((chapter) => !hasStoredReview(chapter))) return null;
  if (chapters.length === 1) return reviewPayloadFromChapter(chapters[0]);

  const scoreItems = chapters.map(reviewScoreFromChapter).filter(Number.isFinite);
  const score = scoreItems.length ? Math.round(scoreItems.reduce((sum, item) => sum + item, 0) / scoreItems.length) : undefined;
  const dimensions = reviewDimensionDefaults.map(([name, defaultComment]) => {
    const matchedScores = chapters
      .map((chapter) => (chapter.review?.dimensions || []).find((item) => String(item.name || '').includes(name) || name.includes(String(item.name || ''))))
      .filter(Boolean)
      .map((item) => Number(item?.score))
      .filter(Number.isFinite);
    const itemScore = matchedScores.length
      ? Math.round(matchedScores.reduce((sum, item) => sum + item, 0) / matchedScores.length)
      : (score ?? Number.NaN);
    return {
      name,
      score: itemScore,
      passed: Number.isFinite(itemScore) && itemScore >= 80,
      status: Number.isFinite(itemScore) && itemScore >= 80 ? '通过' : '待修',
      comment: `${chapters.length}章${defaultComment}`,
      suggestion: '',
    };
  });
  const problems = chapters.flatMap((chapter) => (
    (chapter.review?.problems || []).map((item) => ({
      ...item,
      location: item.location || `第 ${String(chapter.chapter_no || '').padStart(2, '0')} 章`,
    }))
  ));
  return { score, dimensions, problems };
};

const stagePromptCopy: Record<StageKey, { title: string; body: string; aiLabel: string; manualLabel: string }> = {
  inspiration: {
    title: '',
    body: '',
    aiLabel: '',
    manualLabel: '',
  },
  settings: {
    title: '当前还没有故事设定',
    body: '可以自己填写题材、文风、节奏和篇幅，也可以让 AI 根据灵感梗概自动生成一版设定。',
    aiLabel: 'AI生成',
    manualLabel: '手动填写',
  },
  characters: {
    title: '当前还没有人物档案',
    body: '可以自己录入人物，也可以让 AI 根据设定生成人物目标、秘密和关系。',
    aiLabel: 'AI生成',
    manualLabel: '手动填写',
  },
  outline: {
    title: '当前还没有大纲',
    body: '可以自己搭建总纲、章纲和伏笔，也可以让 AI 根据人物关系生成一版完整大纲。',
    aiLabel: 'AI生成',
    manualLabel: '手动填写',
  },
  chapters: {
    title: '当前还没有章节计划',
    body: '可以先手动查看空白编辑器，也可以让 AI 根据大纲生成章节计划。',
    aiLabel: 'AI生成',
    manualLabel: '手动填写',
  },
  review: {
    title: '还没有可审稿章节',
    body: '请先生成章节正文，再回到这里审稿。',
    aiLabel: '',
    manualLabel: '',
  },
  publish: {
    title: '',
    body: '',
    aiLabel: '',
    manualLabel: '',
  },
};

const StageDecisionCard: React.FC<{
  stage: StageKey;
  title?: string;
  body?: string;
  primaryLabel?: string;
  secondaryLabel?: string;
  onPrimary?: () => void;
  onSecondary?: () => void;
}> = ({ stage, title, body, primaryLabel, secondaryLabel, onPrimary, onSecondary }) => {
  const copy = stagePromptCopy[stage];
  return (
    <section className="stage-decision-card">
      <div className="stage-decision-dialog">
        <strong>{title || copy.title}</strong>
        <p>{body || copy.body}</p>
        <div className="stage-decision-actions">
          {secondaryLabel && onSecondary && <button type="button" onClick={onSecondary}>{secondaryLabel}</button>}
          {primaryLabel && onPrimary && <button className="primary" type="button" onClick={onPrimary}>{primaryLabel}</button>}
        </div>
      </div>
    </section>
  );
};

const initialCharacterProfiles: CharacterProfile[] = [
  {
    id: 'axing',
    name: '阿杏',
    role: '主角',
    faction: '主角',
    age: '24',
    identity: '旧宅继承人，父亲禁令的继承者',
    goal: '查清母亲失踪和三点零八分旧钟的真相。',
    personality: '敏感、克制、记忆力强，遇到亲情问题会先自责再行动。',
    speech: '少用夸张语气，多用短句和追问；紧张时会重复确认时间。',
    secret: '她小时候曾经启动过老钟，但记忆被父亲抹掉。',
    requirement: '任何章节里，她的行动都必须围绕母亲失踪、父亲禁令或旧钟代价推进。',
    arc: '从被禁令保护的人，变成主动承担时间债的人。',
    x: 30,
    y: 50,
  },
  {
    id: 'father',
    name: '父亲',
    role: '保护者 / 隐瞒者',
    faction: '亲属',
    age: '54',
    identity: '阿杏父亲，旧钟禁令的执行者',
    goal: '阻止阿杏再次接触老钟，避免时间债落到她身上。',
    personality: '沉默、强硬、习惯替别人决定代价。',
    speech: '命令式短句多，不解释原因，只强调“不要”。',
    secret: '他不是凶手，而是替阿杏偿还过一次时间债。',
    requirement: '不能写成单纯坏父亲；每次严厉背后都要有保护逻辑。',
    arc: '从被误解的阻碍者，回收为牺牲型守门人。',
    x: 13,
    y: 35,
  },
  {
    id: 'mother',
    name: '母亲',
    role: '失踪核心',
    faction: '亲属',
    age: '未知',
    identity: '旧案失踪者，旧信真正收件人',
    goal: '把阿杏从第一次启动老钟的后果里救出来。',
    personality: '温和但决绝，关键选择比父亲更狠。',
    speech: '信件语气克制，不直接煽情，常留下双关线索。',
    secret: '她并没有死，而是停留在某个被旧钟锁住的时间层。',
    requirement: '只通过信件、画像、物件和回忆推进，前期不直接出场。',
    arc: '从失踪谜团变成规则真相的钥匙。',
    x: 10,
    y: 22,
  },
  {
    id: 'grandma',
    name: '祖母',
    role: '线索保存者',
    faction: '亲属',
    age: '78',
    identity: '阁楼遗物和青铜怀表的保存者',
    goal: '把真相拆散藏进遗物，等待阿杏有能力承受。',
    personality: '慈祥表象下极有秩序感，做事留三重保险。',
    speech: '旧式表达，不直接说破因果。',
    secret: '她曾经和谢知信共同封存老钟。',
    requirement: '祖母相关线索必须可回查，不能变成随手塞答案。',
    arc: '从温情回忆变成旧约参与者。',
    x: 52,
    y: 24,
  },
  {
    id: 'xie',
    name: '谢知信',
    role: '主要对手',
    faction: '敌对',
    age: '31',
    identity: '钟楼研究者，旧约继承人',
    goal: '夺回老钟控制权，让被封存的时间层重新打开。',
    personality: '礼貌、精准、冷静，习惯把人当变量。',
    speech: '像讲课一样拆解对方选择，很少直接威胁。',
    secret: '他和阿杏家不是仇人，而是同一场旧约的两端。',
    requirement: '不能只做脸谱反派；他每次出手都要推进规则揭露。',
    arc: '从敌人变成旧约真相的镜像主角。',
    x: 72,
    y: 50,
  },
  {
    id: 'craftsman',
    name: '老钟匠',
    role: '规则知情人',
    faction: '谜团',
    age: '69',
    identity: '修钟人，谢知信旧日同谋',
    goal: '确认阿杏是否值得知道完整规则。',
    personality: '圆滑、惜命、知道太多所以说话绕。',
    speech: '常用手艺比喻时间规则。',
    secret: '他修过的不是钟，而是钟里的一段关系。',
    requirement: '每次出现必须给出一个可验证规则，不做纯谜语人。',
    arc: '从旁观者变成关键证人。',
    x: 90,
    y: 26,
  },
  {
    id: 'su',
    name: '苏婉',
    role: '闺蜜',
    faction: '盟友',
    age: '24',
    identity: '阿杏大学同学，现实线支持者',
    goal: '把阿杏从旧宅和幻觉里拉回现实。',
    personality: '外向、直觉强、嘴硬心软。',
    speech: '现代口语，负责打断过度解释。',
    secret: '她家也收到过三点零八分相关的空白信。',
    requirement: '不能只做吐槽工具人，她要承担现实调查线。',
    arc: '从旁观闺蜜变成现实线破局者。',
    x: 10,
    y: 62,
  },
  {
    id: 'cousin',
    name: '表哥',
    role: '堂亲',
    faction: '亲属',
    age: '29',
    identity: '负责处理祖宅产权的亲戚',
    goal: '尽快卖掉祖宅，切断旧案牵连。',
    personality: '现实、功利，但不是恶人。',
    speech: '算账式表达，喜欢讲成本。',
    secret: '他知道父亲生前曾找过买家，其实是在找守钟人。',
    requirement: '他制造阻力要来自现实利益，不要突然黑化。',
    arc: '从阻碍者变成现实证据提供者。',
    x: 22,
    y: 76,
  },
  {
    id: 'chen',
    name: '陈老头',
    role: '半师长',
    faction: '师长',
    age: '66',
    identity: '档案馆退休管理员',
    goal: '帮阿杏查旧邮戳，同时保护档案秘密。',
    personality: '谨慎、爱面子、怕惹事。',
    speech: '官腔和市井话混用。',
    secret: '他年轻时见过母亲最后一次出现。',
    requirement: '给信息必须换代价，避免无条件送线索。',
    arc: '从怕事老人变成旧案证人。',
    x: 52,
    y: 78,
  },
  {
    id: 'gu',
    name: '顾屿',
    role: '恋人候选',
    faction: '谜团',
    age: '27',
    identity: '钟楼博物馆策展助理',
    goal: '接近阿杏，确认她是否还记得小时候的事。',
    personality: '温和、观察力强、习惯隐藏立场。',
    speech: '少承诺，多反问。',
    secret: '他小时候和阿杏一起见过停走前的老钟。',
    requirement: '感情线必须服务真相线，不写独立恋爱副本。',
    arc: '从暧昧对象变成共同证人。',
    x: 30,
    y: 26,
  },
  {
    id: 'zhou',
    name: '周警官',
    role: '调查压力',
    faction: '盟友',
    age: '38',
    identity: '负责旧宅失踪案复查的警官',
    goal: '用现实证据解释旧案，压制玄学解释。',
    personality: '理性、强势、对证据极敏感。',
    speech: '直接、带审讯节奏。',
    secret: '他的师父当年压下过母亲失踪案。',
    requirement: '他负责现实逻辑校验，不能轻易相信超自然。',
    arc: '从怀疑者变成现实线盟友。',
    x: 70,
    y: 84,
  },
  {
    id: 'robe',
    name: '蓝袍人',
    role: '幕后执行者',
    faction: '敌对',
    age: '未知',
    identity: '替谢知信执行追索的人',
    goal: '追回血债凭据和青铜怀表。',
    personality: '沉默、极端守约。',
    speech: '几乎不解释，只说契约词。',
    secret: '他不是活人，而是某次回溯留下的空壳。',
    requirement: '出场次数少但压迫感强，每次出现都要改变局面。',
    arc: '从追杀者变成时间债具象化。',
    x: 92,
    y: 64,
  },
];

const initialCharacterRelations: CharacterRelationTuple[] = [
  ['mother', 'father', '夫妻', 'kin'],
  ['mother', 'axing', '母女 · 失踪', 'kin'],
  ['father', 'axing', '父女', 'kin'],
  ['su', 'axing', '闺蜜', 'friend'],
  ['cousin', 'axing', '堂亲', 'kin'],
  ['gu', 'axing', '恋人', 'mystery'],
  ['grandma', 'axing', '祖孙 · 已逝', 'kin'],
  ['grandma', 'xie', '旧识 · 民国', 'mystery'],
  ['chen', 'axing', '师徒', 'mentor'],
  ['chen', 'xie', '师门', 'mentor'],
  ['craftsman', 'xie', '同谋', 'mystery'],
  ['zhou', 'cousin', '上下级', 'friend'],
  ['xie', 'axing', '对立 · 跨时空宿敌', 'enemy'],
  ['robe', 'xie', '指使', 'enemy'],
  ['robe', 'axing', '追索', 'enemy'],
];

const factionLabels = ['主角', '亲属', '盟友', '师长', '谜团', '敌对'];

const initialOutlineChapters: OutlineChapter[] = Array.from({ length: 30 }, (_, index) => {
  const no = index + 1;
  const titles = [
    '序 · 钟停了',
    '阁楼的信封',
    '邮戳是民国',
    '母亲的画像',
    '阁楼遗物',
    '第一次回去',
    '空白的三分钟',
    '父亲的第三条禁令',
    '钟楼第十三层',
    '谢知信来访',
    '旧约的名字',
    '青铜怀表裂开',
    '钟摆裂纹',
    '苏婉收到空白信',
    '档案馆火痕',
    '老钟匠的价码',
    '蓝袍人的追索',
    '母亲最后一次出现',
    '顾屿的童年照片',
    '第一次失败的回溯',
    '被改写的族谱',
    '谢知信的镜像',
    '时间债清单',
    '父亲替她偿还的夜晚',
    '第十三层重开',
    '母亲在钟里',
    '旧约反转',
    '三点零八分之前',
    '最后一次上发条',
    '钟声重新响起',
  ];
  return {
    id: `outline-${String(no).padStart(2, '0')}`,
    no,
    title: titles[index],
    purpose: no <= 10 ? '建立旧宅、禁令、旧信和主角目标。' : no <= 20 ? '扩大时间债代价，推进人物关系反转。' : '回收伏笔，揭示旧约真相并完成主角选择。',
    summary: no <= 10
      ? '阿杏在旧宅线索中逐步确认三点零八分不是怪谈，而是家族旧约留下的时间节点。'
      : no <= 20
        ? '阿杏尝试使用旧钟回溯，但每次收益都会带来记忆损耗和现实裂纹。'
        : '所有旧信、怀表、画像和钟楼线索汇合，阿杏必须决定是否替下一代承担时间债。',
    hook: no % 3 === 0 ? '章末抛出新物证。' : no % 3 === 1 ? '章末出现人物反转。' : '章末制造规则代价。',
    foreshadow: no === 5 ? '埋：钟摆裂纹' : no === 13 ? '回收：钟摆裂纹' : no === 18 ? '埋：母亲最后一次出现' : no === 26 ? '回收：母亲在钟里' : '',
  };
});

const initialForeshadowPlans: ForeshadowPlan[] = [
  { id: 'f-01', name: '钟摆裂纹', plantChapter: 5, revealChapter: 13, state: '已规划', rule: '每次回溯都会让现实留下裂纹，后续必须影响人物选择。' },
  { id: 'f-02', name: '民国邮戳', plantChapter: 2, revealChapter: 15, state: '已规划', rule: '邮戳不是穿越装饰，而是旧约时间层的入口证明。' },
  { id: 'f-03', name: '父亲第三条禁令', plantChapter: 1, revealChapter: 24, state: '待强化', rule: '禁令必须在父亲牺牲真相出现前反复制造阻力。' },
  { id: 'f-04', name: '青铜怀表', plantChapter: 5, revealChapter: 22, state: '已规划', rule: '怀表是时间债凭据，不能只做线索道具。' },
  { id: 'f-05', name: '母亲画像背面', plantChapter: 4, revealChapter: 26, state: '待确认', rule: '画像背面必须指向母亲仍在钟里的核心真相。' },
];

const initialInspirations: SynopsisDraft[] = [
  {
    id: 'idea-clock',
    seed: '一个女孩回到祖宅，发现所有钟都停在三点零八分。',
    title: '《三点零八分的钟》',
    premise: '阿杏回到废弃多年的祖宅，发现家中所有钟表都停在三点零八分。父亲临终前留下三条禁令：不要上阁楼、不要拆民国邮戳的旧信、不要让老钟再次走动。她偏偏在阁楼樟木箱底找到一封写给自己的三十年前来信。',
    stakes: '每一次让老钟重新走动，阿杏都只能回到 108 秒前，却会丢失一段亲情记忆。她越接近母亲失踪的真相，越分不清自己是在救家人，还是亲手把家人从历史里擦掉。',
    conflict: '真相 vs 亲情记忆',
    hook: '旧信、禁令、时间债',
    boundary: '不使用万能玄学解释',
    status: '已成项目',
    updatedAt: '2 分钟前',
    sortTime: Date.now() - 2 * 60 * 1000,
  },
  {
    id: 'idea-book',
    seed: '一个落榜生在旧书摊买到一本会自动续写自己未来三天的小说。',
    title: '《会续写未来的旧书》',
    premise: '落榜生周驰在旧书摊买到一本空白小说，第二天醒来发现书里自动写出了自己未来三天的失败。只要照着书里的警告避开灾祸，他就能得到一次反超命运的机会。',
    stakes: '书里的每次续写都会把另一个人的选择抹掉。周驰越想逆袭，越像是在偷走身边人的人生，直到他发现最新章节的作者署名竟然是未来的自己。',
    conflict: '逆袭欲望 vs 他人人生',
    hook: '未来章节、旧书作者、三日倒计时',
    boundary: '不把预言能力写成无成本外挂',
    status: '灵感草稿',
    updatedAt: '今天',
    sortTime: Date.now() - 3 * 60 * 60 * 1000,
  },
  {
    id: 'idea-food',
    seed: '一个外卖员每天凌晨三点都会收到来自未来的订单。',
    title: '《凌晨三点的订单》',
    premise: '外卖员陈野连续七天收到来自未来的订单，每一单都指向一场即将发生的事故。他起初只是想靠提前送达赚钱，后来发现每份订单都是某个陌生人临终前发出的求救。',
    stakes: '每救下一人，下一张订单的收件人就会离他更近。第七单出现了母亲的名字，而订单地址是他从未去过的废弃医院。',
    conflict: '赚钱机会 vs 救人代价',
    hook: '未来订单、七日递进、母亲地址',
    boundary: '不靠巧合连续救场',
    status: '待评估',
    updatedAt: '昨天',
    sortTime: Date.now() - 24 * 60 * 60 * 1000,
  },
];

const emptySynopsisDraft: SynopsisDraft = {
  id: 'idea-empty-placeholder',
  seed: '',
  title: '《未命名新书》',
  premise: '',
  stakes: '',
  conflict: '',
  hook: '',
  boundary: '',
  status: '空白项目',
  updatedAt: '等待同步',
  sortTime: 0,
};

const ideaPresets = [
  '一个落榜生在旧书摊买到一本会自动续写自己未来三天的小说。',
  '所有人醒来都会忘记昨晚，只有主角能看见城市上空的倒计时。',
  '一个外卖员凌晨三点收到来自未来的订单，收件人是已经去世的人。',
  '祖宅里的旧钟每走一秒，就会从家族相册里抹掉一个人。',
  '一个游戏策划发现自己写废的副本，正在现实里逐个上线。',
  '失眠医生每晚都会进入病人的梦里，却在梦里见到同一个童年自己。',
];

const buildSynopsis = (seed: string): SynopsisDraft => {
  const compactSeed = seed.trim().replace(/\s+/g, '');
  const titleSource = compactSeed.slice(0, 10) || '新故事';

  if (compactSeed.includes('三点零八') || compactSeed.includes('钟')) {
    return {
      id: `idea-${Date.now()}`,
      seed,
      title: '《三点零八分的钟》',
      premise: '阿杏回到废弃多年的祖宅，发现家中所有钟表都停在三点零八分。父亲临终前留下三条禁令：不要上阁楼、不要拆民国邮戳的旧信、不要让老钟再次走动。她偏偏在阁楼樟木箱底找到一封写给自己的三十年前来信，信中说父亲并不是隐瞒真相的人，而是一直在替她偿还时间债。',
      stakes: '为了查清母亲失踪和旧钟的关系，阿杏开始尝试让老钟重新走动。每一次回溯只能回到 108 秒前，却会让现实留下一道“钟摆裂纹”，也会从她记忆里抹去一段亲情细节。越接近真相，她越分不清自己是在拯救家人，还是亲手把家人从历史里擦掉。',
      conflict: '真相 vs 亲情记忆',
      hook: '旧信、禁令、时间债',
      boundary: '不使用万能玄学解释',
      status: '新灵感',
      updatedAt: '刚刚',
      sortTime: Date.now(),
    };
  }

  return {
    id: `idea-${Date.now()}`,
    seed,
    title: `《${titleSource}》`,
    premise: `主角因为“${seed.trim()}”被卷入一条无法回头的主线。故事开场先给出异常事件，再让主角在三次选择中逐步发现：这件事不是偶然，而是某个旧承诺、旧秘密或旧规则正在重新生效。`,
    stakes: '主角每推进一步，都会得到一份短期收益，同时失去一份长期安全感。中段用人物关系反转和规则代价扩大压力，后段把最初的一句话灵感回收成最终真相，让读者知道这本书从第一章开始就没有跑偏。',
    conflict: '欲望 vs 代价',
    hook: '异常开局、三次选择、旧秘密',
    boundary: '不靠巧合强行推进剧情',
    status: '新灵感',
    updatedAt: '刚刚',
    sortTime: Date.now(),
  };
};

const SectionTitle: React.FC<{ icon: React.ReactNode; title: string; desc?: string }> = ({ icon, title, desc }) => (
  <div className="stage-section-title">
    {icon}
    <div>
      <strong>{title}</strong>
      {desc && <span>{desc}</span>}
    </div>
  </div>
);

const parseSettingValue = (value: string) => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const settingsFromItems = (items: BackendSetting[], project?: BackendProject): Partial<GeneratedSettings> => {
  const map = Object.fromEntries(items.map((item) => [item.setting_key, parseSettingValue(item.setting_value)]));
  return {
    genre: String(map.genre || project?.genre || ''),
    style_platform: String(map.style_platform || project?.platform || ''),
    style_name: String(map.style_name || ''),
    style_note: String(map.style_note || items.find((item) => item.setting_key === 'style_platform')?.note || ''),
    narrative_perspective: normalizeNarrativePerspective(
      map.narrative_perspective
      || map.perspective
      || project?.settings?.narrative_perspective
      || project?.settings?.perspective
      || '',
    ),
    rhythms: Array.isArray(map.rhythms) ? map.rhythms.map(String) : [],
    chapter_target: Number(map.chapter_target || project?.chapter_target || 0),
    initial_chapter_count: Number(map.initial_chapter_count || project?.settings?.initial_chapter_count || Math.min(5, Number(map.chapter_target || project?.chapter_target || 5))),
    words_per_chapter: Number(map.words_per_chapter || project?.words_per_chapter || 0),
    chapter_word_limit: Number(map.chapter_word_limit || map.words_per_chapter || project?.words_per_chapter || 0),
    boundary: String(map.boundary || ''),
  };
};

const StagePanels: React.FC<StagePanelProps> = ({
  activeTab,
  projectId,
  activeInspirationId,
  inspirationItems = [],
  generationSettings,
  workbenchChapters = [],
  stageCompletion,
  score,
  setToast,
  onInspirationTitleChange,
  onOpenChapters,
  onOpenCharacters,
  onOpenOutline,
  onOpenSettings,
  onOpenPublish,
  onStageCompletionChange,
  onWorkbenchRefresh,
  onInspirationSelect,
  onEnsureWorkbenchReady,
  onAssetsRefresh,
  onRequireLogin,
  onGenerationFailure,
}) => {
  const [ideaText, setIdeaText] = useState('');
  const [inspirations, setInspirations] = useState<SynopsisDraft[]>([]);
  const [activeIdeaId, setActiveIdeaId] = useState('');
  const ideaLoading = Boolean(localStorage.getItem('lingbi-token') && !projectId);
  const [ideaGenerating, setIdeaGenerating] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [streamingFields, setStreamingFields] = useState<string[]>([]);
  const [generationErrors, setGenerationErrors] = useState<Partial<Record<StageKey, string>>>({});
  const [saveState, setSaveState] = useState('请先生成或填写灵感');
  const [dirtyDraftIds, setDirtyDraftIds] = useState<Record<string, boolean>>({});
  const [flowSourceIdeaId, setFlowSourceIdeaId] = useState<string | null>(null);
  const [downstreamSavedState, setDownstreamSavedState] = useState<Partial<Record<StageKey, boolean>>>({});
  const [manualStages, setManualStages] = useState<Partial<Record<StageKey, boolean>>>({});
  const [stageLoading, setStageLoading] = useState<Partial<Record<StageKey, boolean>>>({});
  const [stageDataLoaded, setStageDataLoaded] = useState<Partial<Record<StageKey, boolean>>>({});
  const [stageDataSource, setStageDataSource] = useState<Partial<Record<StageKey, string>>>({});
  const [saveBusy, setSaveBusy] = useState(false);
  const [settingsGenerating, setSettingsGenerating] = useState(false);
  const [settingGenerationText, setSettingGenerationText] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [selectedGenre, setSelectedGenre] = useState('');
  const [customGenre, setCustomGenre] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('通用');
  const [styleName, setStyleName] = useState('');
  const [styleNote, setStyleNote] = useState('');
  const [narrativePerspective, setNarrativePerspective] = useState('');
  const [selectedRhythms, setSelectedRhythms] = useState<string[]>([]);
  const [chapterTarget, setChapterTarget] = useState(50);
  const [initialChapterCount, setInitialChapterCount] = useState(5);
  const [defaultChapters, setDefaultChapters] = useState(50);
  const [maxChapters, setMaxChapters] = useState(200);
  const [defaultWordsPerChapter, setDefaultWordsPerChapter] = useState(1000);
  const [publishMinScore, setPublishMinScore] = useState(80);
  const [wordsTarget, setWordsTarget] = useState(1000);
  const [chapterWordLimit, setChapterWordLimit] = useState(1450);
  const [boundary, setBoundary] = useState('');
  const [characterProfiles, setCharacterProfiles] = useState<CharacterProfile[]>([]);
  const [characterRelations, setCharacterRelations] = useState<CharacterRelationTuple[]>([]);
  const [activeCharacterId, setActiveCharacterId] = useState('empty-character');
  const [charactersGenerating, setCharactersGenerating] = useState(false);
  const [characterGenerationText, setCharacterGenerationText] = useState('');
  const [relationFilter, setRelationFilter] = useState<CharacterProfile['faction'] | null>(null);
  const [masterOutline, setMasterOutline] = useState('');
  const [outlineChapters, setOutlineChapters] = useState<OutlineChapter[]>([]);
  const [activeOutlineId, setActiveOutlineId] = useState('outline-empty');
  const [outlineGenerating, setOutlineGenerating] = useState(false);
  const [outlineGenerationText, setOutlineGenerationText] = useState('');
  const [chaptersPlanning, setChaptersPlanning] = useState(false);
  const [chaptersPlanningText, setChaptersPlanningText] = useState('');
  const [foreshadowPlans, setForeshadowPlans] = useState<ForeshadowPlan[]>([]);
  const [reviewChapterNo, setReviewChapterNo] = useState(1);
  const [reviewMode, setReviewMode] = useState('单章精审');
  const [reviewChapters, setReviewChapters] = useState<BackendChapterSnapshot[]>([]);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [repairError, setRepairError] = useState('');
  const [, setReviewResult] = useState<ReviewPayload | null>(null);
  const [repairStates, setRepairStates] = useState<Record<string, '待修复' | '修复中' | '已修复'>>({});
  const [repairBusy, setRepairBusy] = useState(false);
  const [repairProgressText, setRepairProgressText] = useState('');
  const [publishTitle, setPublishTitle] = useState('');
  const [publishAuthor, setPublishAuthor] = useState('灵笔作者');
  const [publishDesc, setPublishDesc] = useState('');
  const [publishBookStatus, setPublishBookStatus] = useState<'连载中' | '已完结'>('连载中');
  const [publishChannel, setPublishChannel] = useState<'男频' | '女频'>('男频');
  const [publishCategory, setPublishCategory] = useState('');
  const [publishCategories, setPublishCategories] = useState<LibraryCategoryOption[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [descriptionGenerating, setDescriptionGenerating] = useState(false);
  const [coverGenerating, setCoverGenerating] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [publishStateText, setPublishStateText] = useState('');
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState('');
  const [coverState, setCoverState] = useState('AI 封面待生成');
  const [coverProgress, setCoverProgress] = useState(0);
  const [publishSnapshot, setPublishSnapshot] = useState<WorkbenchSnapshot | null>(null);
  const activeDraft = useMemo(
    () => inspirations.find((item) => item.id === activeIdeaId) || inspirations[0] || emptySynopsisDraft,
    [activeIdeaId, inspirations],
  );
  const activePublishInspirationId = activeDraft?.backendId || activeInspirationId || null;
  const activePublishDraft = useMemo(
    () => inspirations.find((item) => item.backendId === activePublishInspirationId) || activeDraft,
    [activeDraft, activePublishInspirationId, inspirations],
  );
  const activeDraftRef = useRef(activeDraft);
  const stageCompletionRef = useRef(stageCompletion);
  const workflowInFlightRef = useRef<Record<string, Promise<WorkflowStatusResponse | null>>>({});
  const stageSnapshotCacheRef = useRef<Map<string, { snapshot: WorkbenchSnapshot; expiresAt: number }>>(new Map());
  const stageSnapshotInFlightRef = useRef<Record<string, Promise<WorkbenchSnapshot>>>({});
  const autoCoverAttemptedRef = useRef<Set<string>>(new Set());
  const lastMismatchResetRef = useRef<string | null>(null);
  const reportedGenerationErrorRef = useRef('');
  useEffect(() => {
    activeDraftRef.current = activeDraft;
  }, [activeDraft]);
  useEffect(() => {
    stageCompletionRef.current = stageCompletion;
  }, [stageCompletion]);

  const invalidateStageSnapshots = useCallback(() => {
    stageSnapshotCacheRef.current.clear();
  }, []);

  const fetchStageSnapshot = useCallback((targetProjectId: number, inspirationId: number) => {
    const key = `${targetProjectId}:${inspirationId}`;
    const cached = stageSnapshotCacheRef.current.get(key);
    if (cached && cached.expiresAt > Date.now()) return Promise.resolve(cached.snapshot);
    if (!stageSnapshotInFlightRef.current[key]) {
      stageSnapshotInFlightRef.current[key] = apiRequest<WorkbenchSnapshot>(
        workbenchPath(targetProjectId, inspirationId),
      ).then((snapshot) => {
        stageSnapshotCacheRef.current.set(key, { snapshot, expiresAt: Date.now() + 30_000 });
        return snapshot;
      }).finally(() => {
        delete stageSnapshotInFlightRef.current[key];
      });
    }
    return stageSnapshotInFlightRef.current[key];
  }, []);

  const refreshWorkbench = useCallback((inspirationId?: number | null) => {
    invalidateStageSnapshots();
    return onWorkbenchRefresh?.(inspirationId);
  }, [invalidateStageSnapshots, onWorkbenchRefresh]);
  useEffect(() => {
    if (!generationSettings) return;
    if (Number(generationSettings.max_chapters) >= 3) setMaxChapters(Number(generationSettings.max_chapters));
    if (Number(generationSettings.default_chapters) >= 3) setDefaultChapters(Number(generationSettings.default_chapters));
    if (Number(generationSettings.default_words_per_chapter) >= 500) {
      const configuredWords = Number(generationSettings.default_words_per_chapter);
      setDefaultWordsPerChapter(configuredWords);
      setWordsTarget((current) => current === 1000 ? configuredWords : current);
      setChapterWordLimit((current) => current === 1450 ? Math.min(10000, configuredWords + 450) : current);
    }
    if (Number(generationSettings.publish_min_score) >= 0 && Number(generationSettings.publish_min_score) <= 100) {
      setPublishMinScore(Number(generationSettings.publish_min_score));
    }
  }, [generationSettings]);
  useEffect(() => {
    const message = [...Object.values(generationErrors), reviewError, repairError]
      .find((item): item is string => Boolean(item?.trim()));
    if (!message) {
      reportedGenerationErrorRef.current = '';
      return;
    }
    if (message === reportedGenerationErrorRef.current) return;
    reportedGenerationErrorRef.current = message;
    onGenerationFailure?.(message);
  }, [generationErrors, onGenerationFailure, repairError, reviewError]);
  const pendingDelete = inspirations.find((item) => item.id === pendingDeleteId);
  const activeCharacter = useMemo(
    () => characterProfiles.find((item) => item.id === activeCharacterId) || characterProfiles[0] || emptyCharacterProfile,
    [activeCharacterId, characterProfiles],
  );
  const activeOutlineChapter = useMemo(
    () => outlineChapters.find((item) => item.id === activeOutlineId) || outlineChapters[0] || emptyOutlineChapter,
    [activeOutlineId, outlineChapters],
  );
  const isActiveDraftDirty = Boolean(dirtyDraftIds[activeDraft.id]);
  const resolveProjectId = useCallback(async () => {
    if (projectId) return projectId;
    setSaveState('正在准备工作台，完成后自动继续...');
    setToast('正在准备工作台，完成后会自动继续');
    if (!onEnsureWorkbenchReady) throw new Error('工作台暂时不可用，请稍后再试');
    return onEnsureWorkbenchReady();
  }, [onEnsureWorkbenchReady, projectId, setToast]);
  const totalWordsEstimate = useMemo(
    () => Math.max(0, Number(chapterTarget || 0) * Number(wordsTarget || 0)),
    [chapterTarget, wordsTarget],
  );

  const applyGeneratedSettings = useCallback((settings: Partial<GeneratedSettings>) => {
    if (settings.genre) {
      const normalizedGenre = normalizeGenreSelection(settings.genre);
      setSelectedGenre(normalizedGenre);
      setCustomGenre(normalizedGenre === '其他' ? String(settings.genre || '').trim() : '');
    }
    if (settings.style_platform) setSelectedStyle(normalizeStyleSelection(settings.style_platform));
    if (settings.style_name) setStyleName(settings.style_name);
    if (settings.style_note) setStyleNote(settings.style_note);
    const normalizedPerspective = normalizeNarrativePerspective(settings.narrative_perspective);
    if (normalizedPerspective) setNarrativePerspective(normalizedPerspective);
    if (settings.rhythms?.length) setSelectedRhythms(normalizeRhythmSelection(settings.rhythms));
    if (settings.chapter_target) setChapterTarget(settings.chapter_target);
    if (settings.initial_chapter_count) setInitialChapterCount(settings.initial_chapter_count);
    if (settings.words_per_chapter) setWordsTarget(settings.words_per_chapter);
    if (settings.chapter_word_limit || settings.words_per_chapter) {
      const target = Number(settings.words_per_chapter || defaultWordsPerChapter);
      const configuredLimit = Number(settings.chapter_word_limit || target + 450);
      setChapterWordLimit(Math.min(configuredLimit, target + 450));
    }
    if (settings.boundary) setBoundary(settings.boundary);
  }, [defaultWordsPerChapter]);

  const applyGeneratedCharacters = useCallback((characters: BackendCharacter[], relations: BackendRelation[] = []) => {
    const profiles = characters.map(toCharacterProfile);
    if (!profiles.length) return;
    setCharacterProfiles(profiles);
    setCharacterRelations(toCharacterRelations(relations, profiles));
    setActiveCharacterId((current) => (profiles.some((item) => item.id === current) ? current : profiles[0].id));
  }, []);

  const applyGeneratedOutline = useCallback((outlines: BackendOutline[], hooks: BackendHook[] = []) => {
    const master = outlines.find((item) => item.outline_type === 'master');
    const chapters = outlines
      .filter((item) => item.outline_type === 'chapter')
      .sort((a, b) => Number(a.chapter_no || 0) - Number(b.chapter_no || 0))
      .map(toOutlineChapter);
    if (master?.summary) setMasterOutline(master.summary);
    if (chapters.length) {
      setOutlineChapters(chapters);
      setActiveOutlineId((current) => (chapters.some((item) => item.id === current) ? current : chapters[0].id));
    }
    if (hooks.length) setForeshadowPlans(hooks.map(toForeshadowPlan));
  }, []);

  const resetSettingsContent = useCallback(() => {
    setSelectedGenre('');
    setCustomGenre('');
    setSelectedStyle('通用');
    setStyleName('');
    setStyleNote('');
    setNarrativePerspective('');
    setSelectedRhythms([]);
    setChapterTarget(defaultChapters);
    setInitialChapterCount(Math.min(5, defaultChapters));
    setWordsTarget(defaultWordsPerChapter);
    setChapterWordLimit(Math.min(10000, defaultWordsPerChapter + 450));
    setBoundary('');
  }, [defaultChapters, defaultWordsPerChapter]);

  const resetCharactersContent = useCallback(() => {
    setCharacterProfiles([]);
    setCharacterRelations([]);
    setActiveCharacterId('empty-character');
    setRelationFilter(null);
  }, []);

  const resetOutlineContent = useCallback(() => {
    setMasterOutline('');
    setOutlineChapters([]);
    setActiveOutlineId('outline-empty');
    setForeshadowPlans([]);
  }, []);

  const resetDownstreamFlow = useCallback((resetCompletion = true) => {
    setSettingsGenerating(false);
    setSettingGenerationText('');
    resetSettingsContent();

    setCharactersGenerating(false);
    setCharacterGenerationText('');
    resetCharactersContent();

    setOutlineGenerating(false);
    setOutlineGenerationText('');
    setChaptersPlanning(false);
    setChaptersPlanningText('');
    resetOutlineContent();

    if (!resetCompletion) return;
    onStageCompletionChange('settings', false);
    onStageCompletionChange('characters', false);
    onStageCompletionChange('outline', false);
    onStageCompletionChange('chapters', false);
    onStageCompletionChange('review', false);
    onStageCompletionChange('publish', false);
  }, [onStageCompletionChange, resetCharactersContent, resetOutlineContent, resetSettingsContent]);

  useEffect(() => {
    setFlowSourceIdeaId(null);
    setDownstreamSavedState({});
    setManualStages({});
    setStageLoading({});
    setDirtyDraftIds({});
    setSaveState('请先生成或填写灵感');
    resetDownstreamFlow(false);
  }, [projectId, resetDownstreamFlow]);

  useEffect(() => {
    setManualStages({});
    setStageLoading({});
    setStageDataLoaded({});
    setStageDataSource({});
    setGenerationErrors({});
    resetDownstreamFlow(false);
  }, [activeDraft.id, resetDownstreamFlow]);

  const applyWorkflowStatus = useCallback((status: WorkflowStatusResponse, draft: SynopsisDraft) => {
    const activeSourceId = status.active_inspiration_id ? `idea-${status.active_inspiration_id}` : null;
    setFlowSourceIdeaId((current) => (current === activeSourceId ? current : activeSourceId));
    const nextSavedState = {
      settings: Boolean(status.stages.settings?.done),
      characters: Boolean(status.stages.characters?.done),
      outline: Boolean(status.stages.outline?.done),
      chapters: Boolean(status.stages.chapters?.done),
      review: Boolean(status.stages.review?.done),
      publish: Boolean(status.stages.publish?.done),
    };
    setDownstreamSavedState((current) => (
      current.settings === nextSavedState.settings
      && current.characters === nextSavedState.characters
      && current.outline === nextSavedState.outline
      && current.chapters === nextSavedState.chapters
      && current.review === nextSavedState.review
      && current.publish === nextSavedState.publish
        ? current
        : nextSavedState
    ));

    (Object.keys(status.stages) as StageKey[]).forEach((key) => {
      const nextDone = Boolean(status.stages[key]?.done);
      if (stageCompletionRef.current[key] !== nextDone) {
        onStageCompletionChange(key, nextDone);
        stageCompletionRef.current = { ...stageCompletionRef.current, [key]: nextDone };
      }
    });

    lastMismatchResetRef.current = activeSourceId && activeSourceId !== draft.id ? `${draft.id}:${activeSourceId}` : null;
  }, [onStageCompletionChange]);

  const syncWorkflowStatus = useCallback(async (draft: SynopsisDraft) => {
    if (!projectId || !localStorage.getItem('lingbi-token') || !draft?.backendId) {
      resetDownstreamFlow(false);
      const nextDone = Boolean(draft && hasDraftContent(draft));
      if (stageCompletionRef.current.inspiration !== nextDone) {
        onStageCompletionChange('inspiration', nextDone);
        stageCompletionRef.current = { ...stageCompletionRef.current, inspiration: nextDone };
      }
      return null;
    }

    invalidateStageSnapshots();
    const requestKey = `${projectId}:${draft.backendId}`;
    if (!workflowInFlightRef.current[requestKey]) {
      workflowInFlightRef.current[requestKey] = apiRequest<WorkflowStatusResponse>(
        `/novel_project_app/projects/${projectId}/workflow-status?inspiration_id=${draft.backendId}`,
      ).finally(() => {
        delete workflowInFlightRef.current[requestKey];
      });
    }
    const status = await workflowInFlightRef.current[requestKey];
    if (status) applyWorkflowStatus(status, draft);
    return status;
  }, [applyWorkflowStatus, invalidateStageSnapshots, onStageCompletionChange, projectId, resetDownstreamFlow]);

  useEffect(() => {
    const nextItems = sortDrafts(inspirationItems.map(toDraft));
    setInspirations(nextItems);
    const nextActive = nextItems.find((item) => item.backendId === activeInspirationId) || nextItems[0];
    setActiveIdeaId(nextActive?.id || '');
    setSaveState(nextActive ? '已保存，可进入设定' : '请先生成或填写灵感');
  }, [activeInspirationId, inspirationItems, projectId]);

  useEffect(() => {
    setDownstreamSavedState({
      settings: Boolean(stageCompletion.settings),
      characters: Boolean(stageCompletion.characters),
      outline: Boolean(stageCompletion.outline),
      chapters: Boolean(stageCompletion.chapters),
      review: Boolean(stageCompletion.review),
      publish: Boolean(stageCompletion.publish),
    });
    setFlowSourceIdeaId(activeInspirationId ? `idea-${activeInspirationId}` : null);
  }, [activeInspirationId, stageCompletion]);

  const persistDraft = useCallback(async (draft: SynopsisDraft, openNext = false, allowBlank = false) => {
    if (!localStorage.getItem('lingbi-token')) {
      onRequireLogin();
      setSaveState('请先登录后保存');
      throw new Error('登录后才能保存灵感');
    }
    if (!allowBlank && !hasDraftContent(draft)) {
      setSaveState('暂无内容可保存');
      if (openNext) setToast('请先生成或填写灵感');
      return draft;
    }

    const targetProjectId = await resolveProjectId();

    const payload = {
      seed: draft.seed,
      // 输入框编辑过程中会短暂为空，自动保存不能把这个中间态提交给后端校验。
      title: draft.title.trim() || '未命名灵感',
      premise: draft.premise.slice(0, SYNOPSIS_LIMIT),
      stakes: draft.stakes,
      conflict: draft.conflict,
      hook: draft.hook,
      boundary: draft.boundary,
      status: draft.status === '空白项目' ? '草稿' : draft.status,
    };
    setSaveBusy(true);
    setSaveState('保存中...');
    try {
      const saved = draft.backendId
        ? await apiRequest<BackendInspiration>(`/novel_project_app/inspirations/${draft.backendId}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        })
        : await apiRequest<BackendInspiration>(`/novel_project_app/projects/${targetProjectId}/inspirations`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      const nextDraft = toDraft(saved);
      setInspirations((items) => {
        const exists = items.some((item) => item.id === draft.id);
        if (!exists) return sortDrafts([nextDraft, ...items]);
        return sortDrafts(items.map((item) => (item.id === draft.id ? nextDraft : item)));
      });
      setActiveIdeaId(nextDraft.id);
      setDirtyDraftIds((items) => {
        const next = { ...items };
        delete next[draft.id];
        next[nextDraft.id] = false;
        return next;
      });
      setSaveState('已保存，可进入设定');
      onStageCompletionChange('inspiration', true);
      if (openNext) onOpenSettings();
      return nextDraft;
    } catch (error) {
      setSaveState('保存失败');
      if (openNext) setToast(error instanceof Error ? error.message : '保存灵感失败');
      throw error;
    } finally {
      setSaveBusy(false);
    }
  }, [onOpenSettings, onRequireLogin, onStageCompletionChange, resolveProjectId, setToast]);

  useEffect(() => {
    if (!isActiveDraftDirty || !activeDraft.backendId || !projectId || !localStorage.getItem('lingbi-token')) return;
    const timer = window.setTimeout(() => {
      void persistDraft(activeDraft).catch((error) => {
        setToast(error instanceof Error ? error.message : '灵感自动保存失败');
      });
    }, 700);
    return () => window.clearTimeout(timer);
  }, [activeDraft, isActiveDraftDirty, persistDraft, projectId, setToast]);

  useEffect(() => {
    onInspirationTitleChange?.(activeDraft.title || '未命名灵感');
  }, [activeDraft.title, onInspirationTitleChange]);

  useEffect(() => {
    if (activeTab !== 'settings' || !projectId || !localStorage.getItem('lingbi-token') || settingsGenerating) return;
    let cancelled = false;
    const loadSettings = async () => {
      setStageLoading((items) => ({ ...items, settings: true }));
      try {
        const draft = activeDraftRef.current;
        if (!draft?.backendId) {
          resetSettingsContent();
          onStageCompletionChange('settings', false);
          return;
        }
        const snapshot = await fetchStageSnapshot(projectId, draft.backendId);
        if (cancelled) return;
        if (!snapshot.settings?.length) {
          resetSettingsContent();
          onStageCompletionChange('settings', false);
          return;
        }
        applyGeneratedSettings(settingsFromItems(snapshot.settings || [], snapshot.project));
        onStageCompletionChange('settings', true);
      } catch (error) {
        if (!cancelled) setToast(error instanceof Error ? error.message : '设定同步失败');
      } finally {
        if (!cancelled) {
          setStageLoading((items) => ({ ...items, settings: false }));
          setStageDataLoaded((items) => ({ ...items, settings: true }));
        }
      }
    };
    loadSettings();
    return () => {
      cancelled = true;
    };
  }, [activeDraft.id, activeDraft.backendId, activeTab, applyGeneratedSettings, fetchStageSnapshot, onStageCompletionChange, projectId, resetSettingsContent, setToast, settingsGenerating]);

  useEffect(() => {
    if (activeTab !== 'characters' || !projectId || !localStorage.getItem('lingbi-token') || charactersGenerating) return;
    let cancelled = false;
    const loadCharacters = async () => {
      setStageLoading((items) => ({ ...items, characters: true }));
      try {
        const draft = activeDraftRef.current;
        if (!draft?.backendId) {
          resetSettingsContent();
          resetCharactersContent();
          onStageCompletionChange('settings', false);
          onStageCompletionChange('characters', false);
          return;
        }
        const snapshot = await fetchStageSnapshot(projectId, draft.backendId);
        if (!cancelled && snapshot.settings?.length) {
          applyGeneratedSettings(settingsFromItems(snapshot.settings || [], snapshot.project));
          onStageCompletionChange('settings', true);
        } else if (!cancelled) {
          resetSettingsContent();
          onStageCompletionChange('settings', false);
        }
        if (cancelled || !snapshot.characters?.length) {
          if (!cancelled) {
            resetCharactersContent();
            onStageCompletionChange('characters', false);
          }
          return;
        }
        applyGeneratedCharacters(snapshot.characters, snapshot.relations || []);
        onStageCompletionChange('characters', true);
      } catch (error) {
        if (!cancelled) setToast(error instanceof Error ? error.message : '人物同步失败');
      } finally {
        if (!cancelled) {
          setStageLoading((items) => ({ ...items, characters: false }));
          setStageDataLoaded((items) => ({ ...items, characters: true }));
        }
      }
    };
    loadCharacters();
    return () => {
      cancelled = true;
    };
  }, [activeDraft.id, activeDraft.backendId, activeTab, applyGeneratedCharacters, applyGeneratedSettings, charactersGenerating, fetchStageSnapshot, onStageCompletionChange, projectId, resetCharactersContent, resetSettingsContent, setToast]);

  useEffect(() => {
    if (activeTab !== 'outline' || !projectId || !localStorage.getItem('lingbi-token') || outlineGenerating || chaptersPlanning) return;
    let cancelled = false;
    const loadOutline = async () => {
      const draft = activeDraftRef.current;
      const dataSource = `${projectId}:${draft?.backendId || 'empty'}`;
      setStageLoading((items) => ({ ...items, outline: true }));
      try {
        if (!draft?.backendId) {
          resetSettingsContent();
          resetCharactersContent();
          resetOutlineContent();
          onStageCompletionChange('settings', false);
          onStageCompletionChange('characters', false);
          onStageCompletionChange('outline', false);
          return;
        }
        const snapshot = await fetchStageSnapshot(projectId, draft.backendId);
        if (!cancelled && snapshot.settings?.length) {
          applyGeneratedSettings(settingsFromItems(snapshot.settings || [], snapshot.project));
          onStageCompletionChange('settings', true);
        } else if (!cancelled) {
          resetSettingsContent();
          onStageCompletionChange('settings', false);
        }
        if (!cancelled && snapshot.characters?.length) {
          applyGeneratedCharacters(snapshot.characters, snapshot.relations || []);
          onStageCompletionChange('characters', true);
        } else if (!cancelled) {
          resetCharactersContent();
          onStageCompletionChange('characters', false);
        }
        if (cancelled || !snapshot.outlines?.length) {
          if (!cancelled) {
            resetOutlineContent();
            onStageCompletionChange('outline', false);
          }
          return;
        }
        applyGeneratedOutline(snapshot.outlines, snapshot.hooks || []);
        onStageCompletionChange('outline', true);
      } catch (error) {
        if (!cancelled) setToast(error instanceof Error ? error.message : '大纲同步失败');
      } finally {
        if (!cancelled) {
          setStageLoading((items) => ({ ...items, outline: false }));
          setStageDataLoaded((items) => ({ ...items, outline: true }));
          setStageDataSource((items) => ({ ...items, outline: dataSource }));
        }
      }
    };
    loadOutline();
    return () => {
      cancelled = true;
    };
  }, [activeDraft.id, activeDraft.backendId, activeTab, applyGeneratedCharacters, applyGeneratedOutline, applyGeneratedSettings, chaptersPlanning, fetchStageSnapshot, outlineGenerating, onStageCompletionChange, projectId, resetCharactersContent, resetOutlineContent, resetSettingsContent, setToast]);

  useEffect(() => {
    const inspirationId = activeDraft.backendId;
    if (!projectId || !inspirationId || !localStorage.getItem('lingbi-token')) return undefined;
    let cancelled = false;
    let timer: number | undefined;

    const setRecovering = (stage: RecoverableGenerationStage, message: string) => {
      const recoveryMessage = `${message || `${RECOVERABLE_STAGE_LABELS[stage]}生成中`}（已在后台继续，可切换页面）`;
      if (stage === 'settings') {
        setSettingsGenerating(true);
        setSettingGenerationText(recoveryMessage);
      } else if (stage === 'characters') {
        setCharactersGenerating(true);
        setCharacterGenerationText(recoveryMessage);
      } else {
        setOutlineGenerating(true);
        setOutlineGenerationText(recoveryMessage);
      }
    };

    const finishRecovery = async (
      stage: RecoverableGenerationStage,
      snapshot: WorkbenchSnapshot,
      failedMessage = '',
    ) => {
      if (stage === 'settings') {
        setSettingsGenerating(false);
        setSettingGenerationText('');
      } else if (stage === 'characters') {
        setCharactersGenerating(false);
        setCharacterGenerationText('');
      } else {
        setOutlineGenerating(false);
        setOutlineGenerationText('');
      }
      if (failedMessage) {
        setGenerationErrors((items) => ({ ...items, [stage]: failedMessage }));
        onStageCompletionChange(stage, false);
        clearRecoverableGeneration();
        setToast(failedMessage);
        return;
      }

      if (stage === 'settings') {
        applyGeneratedSettings(settingsFromItems(snapshot.settings || [], snapshot.project));
      } else if (stage === 'characters') {
        applyGeneratedCharacters(snapshot.characters || [], snapshot.relations || []);
      } else {
        applyGeneratedOutline(snapshot.outlines || [], snapshot.hooks || []);
      }
      setGenerationErrors((items) => ({ ...items, [stage]: '' }));
      setStageDataLoaded((items) => ({ ...items, [stage]: true }));
      setDownstreamSavedState((items) => ({ ...items, [stage]: true }));
      onStageCompletionChange(stage, true);
      clearRecoverableGeneration();
      await syncWorkflowStatus(activeDraftRef.current);
      await refreshWorkbench(inspirationId);
      setToast(`${RECOVERABLE_STAGE_LABELS[stage]}已在后台生成完成，结果已恢复`);
    };

    const pollGeneration = async () => {
      const pending = readRecoverableGeneration();
      if (
        !pending
        || pending.projectId !== projectId
        || pending.inspirationId !== inspirationId
      ) return;
      if (Date.now() - pending.updatedAt > 20 * 60 * 1000) {
        clearRecoverableGeneration();
        return;
      }
      if (pending.status === 'running') setRecovering(pending.stage, pending.message);

      try {
        const snapshot = await apiRequest<WorkbenchSnapshot>(workbenchPath(projectId, inspirationId));
        if (cancelled) return;
        const taskType = RECOVERABLE_TASK_TYPES[pending.stage];
        const task = (snapshot.ai_tasks || []).find((item) => item.task_type === taskType);
        const status = pending.status === 'running' ? task?.status || 'running' : pending.status;
        if (status === 'done') {
          await finishRecovery(pending.stage, snapshot);
          return;
        }
        if (status === 'failed') {
          await finishRecovery(
            pending.stage,
            snapshot,
            task?.error || pending.message || `${RECOVERABLE_STAGE_LABELS[pending.stage]}生成失败，请重试`,
          );
          return;
        }
      } catch {
        // 短暂同步失败不终止原生成请求，下一轮继续查询。
      }
      if (!cancelled) timer = window.setTimeout(pollGeneration, 2000);
    };

    void pollGeneration();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [
    activeDraft.backendId,
    applyGeneratedCharacters,
    applyGeneratedOutline,
    applyGeneratedSettings,
    onStageCompletionChange,
    projectId,
    refreshWorkbench,
    setToast,
    syncWorkflowStatus,
  ]);

  useEffect(() => {
    return () => {
      if (coverPreview?.startsWith('blob:')) URL.revokeObjectURL(coverPreview);
    };
  }, [coverPreview]);

  useEffect(() => {
    if (activeTab !== 'publish') return;
    if (!projectId || !localStorage.getItem('lingbi-token')) return;
    let cancelled = false;
    const sourceId = activePublishInspirationId || undefined;
    const fallbackTitle = (activePublishDraft?.title || '').replace(/[《》]/g, '') || '未命名作品';
    const fallbackDesc = compactDefaultPublishDescription(activePublishDraft?.premise);
    setPublishTitle(fallbackTitle);
    setPublishDesc(fallbackDesc);
    setPublishBookStatus('连载中');
    setCoverUrl('');
    setCoverPreview(null);
    setCoverState('AI 封面待生成');
    setPublishSnapshot(null);
    setPublishStateText('正在同步当前作品发布资料...');
    const loadPublishInfo = async () => {
      try {
        const snapshot = await apiRequest<WorkbenchSnapshot>(workbenchPath(projectId, sourceId, true));
        if (cancelled) return;
        setPublishSnapshot(snapshot);
        const storedUser = JSON.parse(localStorage.getItem('lingbi-user') || 'null') as { username?: string; nickname?: string } | null;
        const currentAuthor = storedUser?.nickname || storedUser?.username || '';
        const publish = snapshot.project.publish || {};
        const snapshotInspiration = snapshot.inspirations?.find((item) => Number(item.id) === Number(sourceId));
        const snapshotTitle = (snapshotInspiration?.title || activePublishDraft?.title || '').replace(/[《》]/g, '');
        const snapshotDesc = compactDefaultPublishDescription(snapshotInspiration?.premise || activePublishDraft?.premise);
        setPublishTitle(publish.title || snapshotTitle || '未命名作品');
        setPublishAuthor(currentAuthor || '灵笔作者');
        setPublishDesc(compactDefaultPublishDescription(publish.description || snapshotDesc));
        setPublishBookStatus(publish.book_status === '已完结' ? '已完结' : '连载中');
        const nextChannel = publish.channel === '女频' ? '女频' : '男频';
        setPublishChannel(nextChannel);
        setPublishCategory(publish.category || '');
        setCoverUrl(publish.cover_url || '');
        if (publish.cover_url) {
          setCoverPreview(publish.cover_url);
          setCoverState('AI 封面已生成');
        } else {
          setCoverPreview(null);
          setCoverState('AI 封面待生成');
        }
        setPublishStateText(publish.published_at ? '已发布到书阁，可再次更新上架信息' : '发布资料已同步，可按需发布到书阁');
      } catch (error) {
        if (!cancelled) setPublishStateText(error instanceof Error ? error.message : '发布信息同步失败');
      }
    };
    void loadPublishInfo();
    return () => {
      cancelled = true;
    };
  }, [activePublishDraft?.premise, activePublishDraft?.title, activePublishInspirationId, activeTab, projectId]);

  useEffect(() => {
    if (activeTab !== 'publish') return;
    let cancelled = false;
    const loadCategories = async () => {
      try {
        const result = await apiRequest<LibraryCategoryOption[]>('/novel_project_app/library/categories');
        if (!cancelled) setPublishCategories(result);
      } catch {
        if (!cancelled) setPublishCategories([]);
      }
    };
    void loadCategories();
    return () => { cancelled = true; };
  }, [activeTab]);

  useEffect(() => {
    const available = publishCategories.filter((item) => item.channel === publishChannel);
    if (available.length && !available.some((item) => item.name === publishCategory)) {
      const suggested = suggestedPublishCategory(publishSnapshot?.project?.genre || selectedGenre, publishChannel);
      setPublishCategory(available.find((item) => item.name === suggested)?.name || available[0].name);
    }
  }, [publishCategories, publishCategory, publishChannel, publishSnapshot?.project?.genre, selectedGenre]);

  const revealDraft = (draft: SynopsisDraft) => {
    setGenerationErrors((items) => ({ ...items, inspiration: '' }));
    setStreamingFields([]);
    ['title', 'premise', 'stakes', 'conflict', 'hook', 'boundary'].forEach((field, index) => {
      window.setTimeout(() => {
        setStreamingFields((items) => [...items, field]);
      }, 160 * (index + 1));
    });
    window.setTimeout(() => {
      setSaveState('已保存，可进入设定');
      setToast(`已生成 ${draft.title} 的项目梗概`);
    }, 1100);
  };

  const generateIdea = async () => {
    if (!onRequireLogin()) return;
    if (!ideaText.trim()) {
      setToast('先输入一句故事灵感');
      return;
    }
    const seed = ideaText.trim();
    setIdeaGenerating(true);
    setGenerationErrors((items) => ({ ...items, inspiration: '' }));
    setSaveState('正在为您生成当中...');
    let targetProjectId: number;
    try {
      targetProjectId = await resolveProjectId();
    } catch (error) {
      setIdeaGenerating(false);
      setSaveState('工作台暂时不可用，可稍后重试');
      setToast(error instanceof Error ? error.message : '工作台暂时不可用，请稍后再试');
      return;
    }
    try {
      let nextDraft: SynopsisDraft;
      {
        setStreamingText('');
        const response = await fetch(`${apiBaseUrl}/novel_project_app/projects/${targetProjectId}/inspirations/generate-stream`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ seed }),
        });
        if (!response.ok || !response.body) {
          throw new Error(await streamErrorMessage(response, 'AI流式生成失败'));
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let doneDraft: SynopsisDraft | null = null;
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (!line.trim()) continue;
            const event = parseStreamEvent(line);
            if (event.type === 'chunk') {
              setStreamingText((text) => text + event.content);
            }
            if (event.type === 'retry') {
              setStreamingText('');
              setSaveState(event.message || '网络波动，AI 正在自动重试...');
            }
            if (event.type === 'done') {
              doneDraft = toDraft(event.inspiration);
            }
            if (event.type === 'error') {
              throw new Error(event.message || 'AI生成失败');
            }
          }
        }
        if (!doneDraft) throw new Error('AI生成结果为空');
        nextDraft = doneDraft;
      }
      setInspirations((items) => sortDrafts([nextDraft, ...items.filter((item) => item.id !== nextDraft.id)]));
      setActiveIdeaId(nextDraft.id);
      resetDownstreamFlow();
      setIdeaText('');
      onStageCompletionChange('inspiration', true);
      await refreshWorkbench(nextDraft.backendId ?? null);
      revealDraft(nextDraft);
    } catch (error) {
      const message = friendlyGenerationError(error, 'AI生成灵感失败');
      setGenerationErrors((items) => ({ ...items, inspiration: message }));
      setToast(message);
      setSaveState('生成失败');
    } finally {
      setIdeaGenerating(false);
    }
  };

  const createBlankProject = async () => {
    if (!onRequireLogin()) return;
    const nextDraft: SynopsisDraft = {
      id: `idea-blank-${Date.now()}`,
      seed: '',
      title: '《未命名新书》',
      premise: '',
      stakes: '',
      conflict: '',
      hook: '',
      boundary: '',
      status: '空白项目',
      updatedAt: '刚刚',
      sortTime: Date.now(),
    };
    setInspirations((items) => sortDrafts([nextDraft, ...items]));
    setActiveIdeaId(nextDraft.id);
    resetDownstreamFlow();
    onStageCompletionChange('inspiration', false);
    setSaveState('正在保存新灵感...');
    try {
      const savedDraft = await persistDraft(nextDraft, false, true);
      await refreshWorkbench(savedDraft.backendId);
      setToast('已新建并保存灵感');
    } catch (error) {
      setSaveState('保存失败，可继续编辑后重试');
      setToast(error instanceof Error ? error.message : '新建灵感保存失败');
    }
  };

  const generateSettingsFromAi = async (draft: SynopsisDraft) => {
    if (!onRequireLogin()) return;
    let targetProjectId: number;
    try {
      targetProjectId = await resolveProjectId();
    } catch (error) {
      setToast(error instanceof Error ? error.message : '工作台暂时不可用，请稍后再试');
      return;
    }
    setFlowSourceIdeaId(draft.id);
    setSettingsGenerating(true);
    setGenerationErrors((items) => ({ ...items, settings: '' }));
    setSettingGenerationText('AI正在准备生成设定...');
    setSelectedGenre('');
    setSelectedStyle('通用');
    setStyleName('');
    setStyleNote('');
    setNarrativePerspective('');
    setSelectedRhythms([]);
    setBoundary('');
    setChapterTarget(defaultChapters);
    setInitialChapterCount(Math.min(5, defaultChapters));
    setWordsTarget(defaultWordsPerChapter);
    setChapterWordLimit(Math.min(10000, defaultWordsPerChapter + 450));
    onStageCompletionChange('settings', false);

    if (!draft.backendId) {
      setSettingsGenerating(false);
      setSettingGenerationText('');
      setToast('请先在灵感页保存灵感，再让 AI 生成设定。');
      return;
    }
    const trackGeneration = (status: RecoverableGenerationState['status'], message: string) => {
      saveRecoverableGeneration({
        projectId: targetProjectId,
        inspirationId: draft.backendId as number,
        stage: 'settings',
        status,
        message,
        updatedAt: Date.now(),
      });
    };
    trackGeneration('running', 'AI正在准备生成设定...');

    try {
      const response = await fetch(`${apiBaseUrl}/novel_project_app/projects/${targetProjectId}/settings/generate-stream`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          inspiration_id: draft.backendId,
          style_platform: selectedStyle,
          chapter_target: clampNumber(chapterTarget, 50, 1, maxChapters),
          initial_chapter_count: clampNumber(initialChapterCount, 5, 1, Math.min(5, chapterTarget)),
          words_per_chapter: wordsTarget,
        }),
      });
      if (!response.ok || !response.body) {
        throw new Error(await streamErrorMessage(response, 'AI流式生成设定失败'));
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.trim()) continue;
          const event = parseStreamEvent(line);
          if (event.type === 'chunk') {
            setSettingGenerationText('AI正在读取灵感并生成设定...');
            trackGeneration('running', 'AI正在读取灵感并生成设定...');
          }
          if (event.type === 'retry') {
            const retryMessage = event.message || '网络波动，AI正在自动重试...';
            setSettingGenerationText(retryMessage);
            trackGeneration('running', retryMessage);
          }
          if (event.type === 'field') {
            setSettingGenerationText(`AI正在写入${event.field}...`);
            trackGeneration('running', `AI正在写入${event.field}...`);
            applyGeneratedSettings({ [event.field]: event.value } as Partial<GeneratedSettings>);
          }
          if (event.type === 'done') {
            trackGeneration('done', '设定已生成并保存');
            applyGeneratedSettings(event.settings);
            setSettingsGenerating(false);
            onStageCompletionChange('settings', true);
            setDownstreamSavedState((items) => ({ ...items, settings: true }));
            void syncWorkflowStatus(draft);
            void refreshWorkbench(draft.backendId);
            setToast('设定已生成并保存');
          }
          if (event.type === 'error') {
            throw new Error(event.message || 'AI生成设定失败');
          }
        }
      }
    } catch (error) {
      const message = friendlyGenerationError(error, 'AI生成设定失败');
      trackGeneration('failed', message);
      setSettingsGenerating(false);
      onStageCompletionChange('settings', false);
      setSettingGenerationText('');
      setGenerationErrors((items) => ({ ...items, settings: message }));
      setToast(message);
    }
  };

  const saveAndOpenSettings = async () => {
    try {
      const savedDraft = await persistDraft(activeDraft);
      await refreshWorkbench(savedDraft.backendId);
      onOpenSettings();
      setToast('灵感已保存，已进入设定');
    } catch (error) {
      setToast(error instanceof Error ? error.message : '保存灵感失败');
    }
  };

  const confirmDeleteIdea = async () => {
    if (!pendingDeleteId) return;
    if (inspirations.length <= 1) {
      setToast('至少保留一个灵感');
      setPendingDeleteId(null);
      return;
    }
    const target = inspirations.find((item) => item.id === pendingDeleteId);
    try {
      if (target?.backendId && localStorage.getItem('lingbi-token')) {
        await apiRequest(`/novel_project_app/inspirations/${target.backendId}`, { method: 'DELETE' });
      }
      const nextItems = inspirations.filter((item) => item.id !== pendingDeleteId);
      setInspirations(nextItems);
      const nextActive = pendingDeleteId === activeDraft.id ? nextItems[0] : activeDraft;
      if (pendingDeleteId === activeDraft.id) {
        setActiveIdeaId(nextActive.id);
        if (pendingDeleteId === flowSourceIdeaId) {
          setFlowSourceIdeaId(null);
          resetDownstreamFlow();
        } else if (flowSourceIdeaId !== nextActive.id) {
          resetDownstreamFlow();
        } else {
          onStageCompletionChange('settings', Boolean(downstreamSavedState.settings));
          onStageCompletionChange('characters', Boolean(downstreamSavedState.characters));
          onStageCompletionChange('outline', Boolean(downstreamSavedState.outline));
          onStageCompletionChange('chapters', Boolean(downstreamSavedState.chapters));
        }
      }
      setToast('已删除灵感');
      if (target?.backendId) await refreshWorkbench(nextActive.backendId);
    } catch (error) {
      setToast(error instanceof Error ? error.message : '删除灵感失败');
      return;
    } finally {
      setPendingDeleteId(null);
    }
  };

  const updateActiveDraft = (patch: Partial<SynopsisDraft>) => {
    const now = Date.now();
    setInspirations((items) =>
      sortDrafts(items.map((item) =>
        item.id === activeDraft.id
          ? { ...item, ...patch, updatedAt: '刚刚', sortTime: now }
          : item,
      )),
    );
    setDirtyDraftIds((items) => ({ ...items, [activeDraft.id]: true }));
    if (activeDraft.id === flowSourceIdeaId) {
      setFlowSourceIdeaId(null);
      setDownstreamSavedState({});
    }
    resetDownstreamFlow();
    onStageCompletionChange('inspiration', false);
    setSaveState('有未保存修改');
  };

  const markManualStage = (stage: StageKey) => {
    setManualStages((items) => ({ ...items, [stage]: true }));
  };

  const startManualSettings = () => {
    if (!selectedGenre.trim()) setSelectedGenre('都市');
    if (!selectedStyle.trim()) setSelectedStyle('通用');
    if (!styleName.trim()) setStyleName('通用文风');
    if (!styleNote.trim()) setStyleNote('清晰、稳定、不绑定平台，优先保证剧情推进和人物一致。');
    if (!selectedRhythms.length) setSelectedRhythms(['强冲突推进']);
    markManualStage('settings');
  };

  const startManualCharacters = () => {
    if (!characterProfiles.length) {
      const next: CharacterProfile = {
        ...emptyCharacterProfile,
        id: 'manual-character-1',
        name: '新人物',
        role: '剧情定位',
        identity: '',
        x: 50,
        y: 50,
      };
      setCharacterProfiles([next]);
      setActiveCharacterId(next.id);
    }
    markManualStage('characters');
  };

  const startManualOutline = () => {
    if (!outlineChapters.length) {
      const next: OutlineChapter = {
        ...emptyOutlineChapter,
        id: 'manual-outline-1',
        title: '第一章',
      };
      setOutlineChapters([next]);
      setActiveOutlineId(next.id);
    }
    markManualStage('outline');
  };

  const markSettingsDirty = () => {
    if (!settingsGenerating) {
      onStageCompletionChange('settings', false);
    }
  };

  const generateCharactersFromAi = async () => {
    if (!onRequireLogin()) return;
    let targetProjectId: number;
    try {
      targetProjectId = await resolveProjectId();
    } catch (error) {
      setToast(error instanceof Error ? error.message : '工作台暂时不可用，请稍后再试');
      return;
    }
    setCharactersGenerating(true);
    setGenerationErrors((items) => ({ ...items, characters: '' }));
    setCharacterGenerationText('AI正在读取设定并拆解人物关系...');
    setCharacterProfiles([]);
    setCharacterRelations([]);
    setActiveCharacterId('empty-character');
    onStageCompletionChange('characters', false);

    if (!activeDraft.backendId) {
      setCharactersGenerating(false);
      setCharacterGenerationText('');
      setToast('请先保存灵感和设定，再让 AI 生成人物。');
      return;
    }
    const generationInspirationId = activeDraft.backendId;
    const trackGeneration = (status: RecoverableGenerationState['status'], message: string) => {
      saveRecoverableGeneration({
        projectId: targetProjectId,
        inspirationId: generationInspirationId,
        stage: 'characters',
        status,
        message,
        updatedAt: Date.now(),
      });
    };
    trackGeneration('running', 'AI正在读取设定并拆解人物关系...');

    try {
      const response = await fetch(`${apiBaseUrl}/novel_project_app/projects/${targetProjectId}/characters/generate-stream`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ inspiration_id: activeDraft.backendId, count: 10 }),
      });
      if (!response.ok || !response.body) {
        throw new Error(await streamErrorMessage(response, 'AI流式生成人物失败'));
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let doneReceived = false;
      const previewCharacters: BackendCharacter[] = [];
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.trim()) continue;
          const event = parseStreamEvent(line);
          if (event.type === 'chunk') {
            setCharacterGenerationText('AI正在设计人物目标、秘密和关系...');
            trackGeneration('running', 'AI正在设计人物目标、秘密和关系...');
          }
          if (event.type === 'retry') {
            const retryMessage = event.message || '网络波动，AI正在自动重试...';
            setCharacterGenerationText(retryMessage);
            trackGeneration('running', retryMessage);
          }
          if (event.type === 'field' && event.field === 'character') {
            const preview = { ...event.value, id: -(Number(event.index || 0) + 1) } as BackendCharacter;
            previewCharacters.push(preview);
            setCharacterGenerationText(`AI正在写入 ${preview.name || '新人物'} 的人物档案...`);
            trackGeneration('running', `AI正在写入 ${preview.name || '新人物'} 的人物档案...`);
            const profiles = previewCharacters.map(toCharacterProfile);
            setCharacterProfiles(profiles);
            if (profiles.length === 1) setActiveCharacterId(profiles[0].id);
          }
          if (event.type === 'done') {
            trackGeneration('done', '人物设定已生成并保存');
            doneReceived = true;
            applyGeneratedCharacters(event.characters || [], event.relations || []);
            setCharactersGenerating(false);
            onStageCompletionChange('characters', true);
            setDownstreamSavedState((items) => ({ ...items, characters: true }));
            void syncWorkflowStatus(activeDraft);
            void refreshWorkbench(activeDraft.backendId);
            setToast('人物设定已生成并保存');
          }
          if (event.type === 'error') {
            throw new Error(event.message || 'AI生成人物失败');
          }
        }
      }
      if (!doneReceived) throw new Error('AI人物生成结果为空');
    } catch (error) {
      const message = friendlyGenerationError(error, 'AI生成人物失败');
      trackGeneration('failed', message);
      setCharactersGenerating(false);
      onStageCompletionChange('characters', false);
      setCharacterGenerationText('');
      setGenerationErrors((items) => ({ ...items, characters: message }));
      setToast(message);
    }
  };

  const saveSettingsAndOpenCharacters = async () => {
    if (!onRequireLogin()) return;
    if (!projectId || !activeDraft.backendId) {
      setToast('请先生成或保存灵感');
      return;
    }
    if (!narrativePerspective) {
      setToast('请先在设定中选择叙事视角');
      return;
    }
    try {
      const safeChapterTarget = clampNumber(chapterTarget, 50, 1, maxChapters);
      const safeInitialChapterCount = clampNumber(initialChapterCount, 5, 1, Math.min(5, safeChapterTarget));
      const safeWordsTarget = clampNumber(wordsTarget, defaultWordsPerChapter, 300, 10000);
      const safeChapterWordLimit = clampNumber(
        chapterWordLimit,
        Math.min(10000, safeWordsTarget + 450),
        safeWordsTarget,
        10000,
      );
      const safeGenre = (selectedGenre === '其他' ? customGenre : selectedGenre).trim() || '都市悬疑';
      const safeStyle = selectedStyle.trim() || '通用';
      if (projectId && localStorage.getItem('lingbi-token')) {
        await apiRequest(`/novel_project_app/projects/${projectId}/settings`, {
          method: 'PUT',
          body: JSON.stringify({
            inspiration_id: activeDraft.backendId,
            genre: safeGenre,
            style_platform: safeStyle,
            style_name: styleName.trim() || `${safeStyle}文风`,
            style_note: styleNote,
            narrative_perspective: narrativePerspective,
            rhythms: selectedRhythms.length ? selectedRhythms.slice(0, 8) : ['短段高钩子', '强冲突推进', '伏笔回收'],
            chapter_target: safeChapterTarget,
            initial_chapter_count: safeInitialChapterCount,
            words_per_chapter: safeWordsTarget,
            chapter_word_limit: safeChapterWordLimit,
            boundary,
          }),
        });
      }
      onStageCompletionChange('settings', true);
      setDownstreamSavedState((items) => ({ ...items, settings: true }));
      await refreshWorkbench(activeDraft.backendId);
      onOpenCharacters();
      setToast('设定已保存，已进入人物');
    } catch (error) {
      setToast(error instanceof Error ? error.message : '保存设定失败');
    }
  };

  const generateOutlineFromAi = async () => {
    if (!onRequireLogin()) return;
    let targetProjectId: number;
    try {
      targetProjectId = await resolveProjectId();
    } catch (error) {
      setToast(error instanceof Error ? error.message : '工作台暂时不可用，请稍后再试');
      return;
    }
    setOutlineGenerating(true);
    setGenerationErrors((items) => ({ ...items, outline: '' }));
    setOutlineGenerationText('AI正在读取人物关系并规划主线...');
    setMasterOutline('');
    setOutlineChapters([]);
    setForeshadowPlans([]);
    onStageCompletionChange('outline', false);

    if (!activeDraft.backendId) {
      setOutlineGenerating(false);
      setOutlineGenerationText('');
      setToast('请先保存灵感、设定和人物，再让 AI 生成大纲。');
      return;
    }
    const generationInspirationId = activeDraft.backendId;
    const trackGeneration = (status: RecoverableGenerationState['status'], message: string) => {
      saveRecoverableGeneration({
        projectId: targetProjectId,
        inspirationId: generationInspirationId,
        stage: 'outline',
        status,
        message,
        updatedAt: Date.now(),
      });
    };
    trackGeneration('running', 'AI正在读取人物关系并规划主线...');

    try {
      const response = await fetch(`${apiBaseUrl}/novel_project_app/projects/${targetProjectId}/outline/generate-stream`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          inspiration_id: activeDraft.backendId,
          chapter_count: clampNumber(initialChapterCount, 5, 1, Math.min(5, chapterTarget)),
        }),
      });
      if (!response.ok || !response.body) {
        throw new Error(await streamErrorMessage(response, 'AI流式生成大纲失败'));
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      const previewChapters: OutlineChapter[] = [];
      const previewHooks: ForeshadowPlan[] = [];
      let doneReceived = false;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.trim()) continue;
          const event = parseStreamEvent(line);
          if (event.type === 'chunk') {
            setOutlineGenerationText('AI正在设计总纲、章节目录和章节钩子...');
            trackGeneration('running', 'AI正在设计总纲、章节目录和章节钩子...');
          }
          if (event.type === 'retry') {
            const retryMessage = event.message || '网络波动，AI正在自动重试...';
            setOutlineGenerationText(retryMessage);
            trackGeneration('running', retryMessage);
          }
          if (event.type === 'field' && event.field === 'master_outline') {
            setMasterOutline(event.value || '');
            setOutlineGenerationText('AI正在写入全书总纲...');
            trackGeneration('running', 'AI正在写入全书总纲...');
          }
          if (event.type === 'field' && event.field === 'chapter') {
            const raw = event.value || {};
            const no = Number(raw.no || Number(event.index || 0) + 1);
            const nextChapter: OutlineChapter = {
              id: `outline-preview-${String(no).padStart(2, '0')}`,
              no,
              title: raw.title || `第${String(no).padStart(2, '0')}章`,
              purpose: raw.purpose || '',
              summary: raw.summary || '',
              hook: raw.hook || '',
              foreshadow: raw.foreshadow || '',
            };
            previewChapters[no - 1] = nextChapter;
            const nextList = previewChapters.filter(Boolean);
            setOutlineChapters(nextList);
            if (nextList.length === 1) setActiveOutlineId(nextList[0].id);
            setOutlineGenerationText(`AI正在写入第 ${String(no).padStart(2, '0')} 章章纲...`);
            trackGeneration('running', `AI正在写入第 ${String(no).padStart(2, '0')} 章章纲...`);
          }
          if (event.type === 'field' && event.field === 'hook') {
            const raw = event.value || {};
            previewHooks[Number(event.index || 0)] = {
              id: `hook-preview-${Number(event.index || 0) + 1}`,
              name: raw.name || '新伏笔',
              plantChapter: Number(raw.plant_chapter || 1),
              revealChapter: Number(raw.reveal_chapter || raw.plant_chapter || 1),
              state: raw.state || '已规划',
              rule: raw.rule || '',
            };
            setForeshadowPlans(previewHooks.filter(Boolean));
            setOutlineGenerationText(`AI正在整理伏笔账本：${raw.name || '新伏笔'}...`);
          }
          if (event.type === 'done') {
            trackGeneration('done', '大纲已生成并保存');
            doneReceived = true;
            applyGeneratedOutline(event.outlines || [], event.hooks || []);
            setOutlineGenerating(false);
            onStageCompletionChange('outline', true);
            setDownstreamSavedState((items) => ({ ...items, outline: true }));
            void syncWorkflowStatus(activeDraft);
            void refreshWorkbench(activeDraft.backendId);
            setToast('大纲已生成并保存');
          }
          if (event.type === 'error') {
            throw new Error(event.message || 'AI生成大纲失败');
          }
        }
      }
      if (!doneReceived) throw new Error('AI大纲生成结果为空');
    } catch (error) {
      const message = friendlyGenerationError(error, 'AI生成大纲失败');
      trackGeneration('failed', message);
      setOutlineGenerating(false);
      onStageCompletionChange('outline', false);
      setOutlineGenerationText('');
      setGenerationErrors((items) => ({ ...items, outline: message }));
      setToast(message);
    }
  };

  const saveCharactersAndOpenOutline = async () => {
    if (!onRequireLogin()) return;
    if (!activeDraft.backendId) {
      setToast('请先生成或保存灵感');
      return;
    }
    onStageCompletionChange('characters', true);
    setDownstreamSavedState((items) => ({ ...items, characters: true }));
    await refreshWorkbench(activeDraft.backendId);
    onOpenOutline();
    setToast('人物设定已保存，已进入大纲');
  };

  const planChaptersFromOutline = async () => {
    if (!onRequireLogin()) return;
    let targetProjectId: number;
    try {
      targetProjectId = await resolveProjectId();
    } catch (error) {
      setToast(error instanceof Error ? error.message : '工作台暂时不可用，请稍后再试');
      return;
    }
    setChaptersPlanning(true);
    setGenerationErrors((items) => ({ ...items, chapters: '' }));
    setChaptersPlanningText('正在把大纲写入章节计划...');

    if (!activeDraft.backendId) {
      setChaptersPlanning(false);
      setChaptersPlanningText('');
      setToast('请先保存当前作品和大纲，再生成章节计划。');
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/novel_project_app/projects/${targetProjectId}/chapters/plan-stream`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ inspiration_id: activeDraft.backendId, target_words: wordsTarget }),
      });
      if (!response.ok || !response.body) {
        throw new Error(await streamErrorMessage(response, '章节计划生成失败'));
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let doneReceived = false;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.trim()) continue;
          const event = parseStreamEvent(line);
          if (event.type === 'field' && event.field === 'chapter') {
            const chapter = event.value || {};
            setChaptersPlanningText(`正在生成第 ${String(chapter.chapter_no || Number(event.index || 0) + 1).padStart(2, '0')} 章计划...`);
          }
          if (event.type === 'done') {
            doneReceived = true;
          }
          if (event.type === 'error') {
            throw new Error(event.message || '章节计划生成失败');
          }
        }
      }
      if (!doneReceived) throw new Error('章节计划生成结果为空');
      onStageCompletionChange('outline', true);
      onStageCompletionChange('chapters', false);
      setDownstreamSavedState((items) => ({ ...items, outline: true, chapters: false }));
      await refreshWorkbench(activeDraft.backendId);
      await syncWorkflowStatus(activeDraft);
      setToast('章节计划已保存，正在进入章节并自动生成第 1 章');
      onOpenChapters();
    } catch (error) {
      const message = friendlyGenerationError(error, '章节计划生成失败');
      setChaptersPlanning(false);
      setChaptersPlanningText('');
      setGenerationErrors((items) => ({ ...items, chapters: message }));
      setToast(message);
      await refreshWorkbench(activeDraft.backendId);
    } finally {
      setChaptersPlanning(false);
      setChaptersPlanningText('');
    }
  };

  const saveOutlineAndOpenChapters = async () => {
    if (!onRequireLogin()) return;
    if (!activeDraft.backendId) {
      setToast('请先生成或保存灵感');
      return;
    }
    setChaptersPlanning(true);
    setChaptersPlanningText('正在确认章节目录...');
    setGenerationErrors((items) => ({ ...items, chapters: '' }));
    try {
      onStageCompletionChange('outline', true);
      setDownstreamSavedState((items) => ({ ...items, outline: true }));
      const targetProjectId = projectId || await resolveProjectId();
      const snapshot = await fetchStageSnapshot(targetProjectId, activeDraft.backendId);
      const hasPlannedChapters = (snapshot.chapters || []).some((chapter) => Boolean(chapter.id));
      if (!hasPlannedChapters) {
        setChaptersPlanning(false);
        await planChaptersFromOutline();
        return;
      }
      onOpenChapters();
      setToast('已进入章节，正在加载目录并准备生成第 1 章');
      await refreshWorkbench(activeDraft.backendId);
    } catch (error) {
      const message = friendlyGenerationError(error, '章节初始化失败');
      setGenerationErrors((items) => ({ ...items, chapters: message }));
      setToast(message);
    } finally {
      setChaptersPlanning(false);
      setChaptersPlanningText('');
    }
  };

  useEffect(() => {
    if (activeTab !== 'review' || !workbenchChapters.length) return;
    const nextChapters = workbenchChapters
      .filter((chapter) => chapter.backendId)
      .map((chapter) => ({
        id: Number(chapter.backendId),
        chapter_no: Number(chapter.index),
        title: chapter.title,
        summary: chapter.summary,
        content: chapter.content,
        words: chapter.words,
        score: chapter.score,
        review: chapter.review,
      }))
      .sort((a, b) => Number(a.chapter_no || 0) - Number(b.chapter_no || 0));
    if (!nextChapters.length) return;
    setReviewChapters((currentChapters) => nextChapters.map((chapter) => {
      const current = currentChapters.find((item) => item.id === chapter.id);
      return {
        ...chapter,
        score: chapter.score !== undefined ? chapter.score : current?.score,
        review: chapter.review !== undefined ? chapter.review : current?.review,
      };
    }));
    const generated = nextChapters.find((chapter) => String(chapter.content || '').trim()) || nextChapters[0];
    setReviewChapterNo((current) => (
      nextChapters.some((chapter) => Number(chapter.chapter_no) === Number(current))
        ? current
        : Number(generated?.chapter_no || nextChapters[0]?.chapter_no || 1)
    ));
    setReviewError('');
  }, [activeTab, workbenchChapters]);

  useEffect(() => {
    const sourceId = activeDraft.backendId || activeInspirationId;
    if (activeTab !== 'review' || !projectId || !sourceId || !localStorage.getItem('lingbi-token')) return;
    let cancelled = false;
    const loadReviewChapters = async () => {
      try {
        setReviewError('');
        const snapshot = await apiRequest<WorkbenchSnapshot>(workbenchPath(projectId, sourceId, true));
        if (cancelled) return;
        const nextChapters = (snapshot.chapters || [])
          .filter((chapter) => Boolean(chapter.id))
          .sort((a, b) => Number(a.chapter_no || 0) - Number(b.chapter_no || 0));
        setReviewChapters(nextChapters);
        const generated = nextChapters.find((chapter) => String(chapter.content || '').trim());
        const selected = nextChapters.find((chapter) => Number(chapter.chapter_no) === reviewChapterNo);
        const nextSelected = selected || generated || nextChapters[0];
        if (nextSelected?.chapter_no) setReviewChapterNo(Number(nextSelected.chapter_no));
        setReviewResult(reviewPayloadFromChapter(nextSelected));
        onStageCompletionChange('review', isReviewStageComplete(nextChapters, publishMinScore));
      } catch (error) {
        if (!cancelled && !workbenchChapters.length) setReviewError(error instanceof Error ? error.message : '审稿数据同步失败');
      }
    };
    loadReviewChapters();
    return () => {
      cancelled = true;
    };
  }, [activeDraft.backendId, activeInspirationId, activeTab, onStageCompletionChange, projectId, publishMinScore, reviewChapterNo, reviewMode, workbenchChapters.length]);

  const selectedReviewChapter = useMemo(
    () => reviewChapters.find((chapter) => Number(chapter.chapter_no) === reviewChapterNo) || reviewChapters[0],
    [reviewChapterNo, reviewChapters],
  );

  const runChapterReview = async () => {
    if (!onRequireLogin()) return;
    if (!selectedReviewChapter?.id || reviewBusy) return;
    if (!String(selectedReviewChapter.content || '').trim()) {
      setReviewError('当前章节还没有正文，请先回到章节页生成正文。');
      return;
    }
    setReviewBusy(true);
    setReviewError('');
    setRepairError('');
    try {
      const result = await apiRequest<ReviewResult>(`/novel_project_app/chapters/${selectedReviewChapter.id}/review`, {
        method: 'POST',
        body: JSON.stringify({
          instruction: reviewMode === '单章精审'
            ? '重点检查是否跑题、人物是否崩、伏笔是否冲突，并给出可执行修复建议。'
            : `按${reviewMode}方式检查章节连续性、伏笔和人物状态。`,
        }),
      });
      setReviewResult(result.review);
      const nextReviewChapters = reviewChapters.map((chapter) => (
        chapter.id === selectedReviewChapter.id
          ? { ...chapter, ...result.chapter, review: result.review, score: result.review.score ?? result.chapter.score }
          : chapter
      ));
      setReviewChapters(nextReviewChapters);
      onStageCompletionChange('review', isReviewStageComplete(nextReviewChapters, publishMinScore));
      await refreshWorkbench(activeDraft.backendId || activeInspirationId);
      setToast(`第 ${String(result.chapter.chapter_no || reviewChapterNo).padStart(2, '0')} 章审稿完成`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'AI审稿失败';
      setReviewError(message);
      setToast(message);
    } finally {
      setReviewBusy(false);
    }
  };

  const updateActiveCharacter = (patch: Partial<CharacterProfile>) => {
    setCharacterProfiles((items) =>
      items.map((item) =>
        item.id === activeCharacter.id
          ? { ...item, ...patch }
          : item,
      ),
    );
  };

  const updateActiveOutlineChapter = (patch: Partial<OutlineChapter>) => {
    setOutlineChapters((items) =>
      items.map((item) =>
        item.id === activeOutlineChapter.id
          ? { ...item, ...patch }
          : item,
      ),
    );
  };

  const updateForeshadowPlan = (id: string, patch: Partial<ForeshadowPlan>) => {
    setForeshadowPlans((items) =>
      items.map((item) => item.id === id ? { ...item, ...patch } : item),
    );
  };

  const runAutoRepair = async () => {
    if (!onRequireLogin()) return;
    if (repairBusy || reviewBusy) return;
    const targetSize = reviewTargetSize(reviewMode);
    const selectedChapterNo = Number(selectedReviewChapter?.chapter_no || reviewChapterNo);
    const targetChapters = selectedReviewChapter
      ? reviewChapters
        .filter((chapter) => Number(chapter.chapter_no || 0) >= selectedChapterNo)
        .sort((a, b) => Number(a.chapter_no || 0) - Number(b.chapter_no || 0))
        .slice(0, targetSize)
      : [];
    const repairTargets = targetChapters.filter((chapter) => {
      const payload = reviewPayloadFromChapter(chapter);
      return Boolean(payload?.problems?.some((item) => item?.message));
    });
    const visibleIssueCount = Math.min(4, repairTargets.reduce(
      (count, chapter) => count + Number(reviewPayloadFromChapter(chapter)?.problems?.length || 0),
      0,
    ));
    const issueIds = Array.from({ length: visibleIssueCount }, (_, index) => `review-${index}`);
    if (!repairTargets.length || !issueIds.length) {
      setToast('暂无可修复的问题');
      return;
    }
    setRepairBusy(true);
    setReviewBusy(true);
    setRepairError('');
    setRepairStates((items) => ({
      ...items,
      ...Object.fromEntries(issueIds.map((id) => [id, '修复中'])),
    }));
    setRepairProgressText(`正在修复 1 / ${repairTargets.length} 章`);
    setToast('正在按问题清单修订正文并重新评分');

    let nextChapters = [...reviewChapters];
    try {
      for (let index = 0; index < repairTargets.length; index += 1) {
        const chapter = repairTargets[index];
        const payload = reviewPayloadFromChapter(chapter);
        const problemLines = (payload?.problems || []).map((problem, problemIndex) => (
          `${problemIndex + 1}. ${problem.location || `第${chapter.chapter_no}章`}：${problem.message}${problem.suggestion ? `；修复要求：${problem.suggestion}` : ''}`
        ));
        const suggestionLines = (payload?.suggestions || []).map((suggestion) => `补充建议：${suggestion}`);
        const instruction = [
          '请针对以下审稿问题小幅修订当前章节正文，必须真正解决问题，同时保持原有剧情主线、叙事人称和章节长度稳定：',
          ...problemLines,
          ...suggestionLines,
        ].join('\n').slice(0, 800);
        setRepairProgressText(`正在修复第 ${String(chapter.chapter_no || 1).padStart(2, '0')} 章（${index + 1} / ${repairTargets.length}）`);

        const response = await fetch(`${apiBaseUrl}/novel_project_app/chapters/${chapter.id}/repair-review-stream`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ instruction, chapter_content: chapter.content }),
        });
        if (!response.ok || !response.body) {
          throw new Error(await streamErrorMessage(response, '一键修复失败'));
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let result: RepairReviewResult | null = null;
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (!line.trim()) continue;
            const event = parseStreamEvent(line);
            if (event.type === 'status' || event.type === 'heartbeat') {
              setRepairProgressText(event.message || `正在处理第 ${chapter.chapter_no} 章`);
            }
            if (event.type === 'done') result = event.result as RepairReviewResult;
            if (event.type === 'error') throw new Error(event.message || '一键修复失败');
          }
        }
        if (!result) throw new Error('修复连接已结束，但没有收到完整结果');
        if (!result.applied || !result.chapter || !result.review) {
          throw new Error(result.message || `第 ${chapter.chapter_no} 章未能自动修复`);
        }
        const repairedChapter = result.chapter;
        const repairedReview = result.review;
        nextChapters = nextChapters.map((item) => (
          item.id === chapter.id
            ? { ...item, ...repairedChapter, review: repairedReview, score: repairedReview.score ?? repairedChapter.score }
            : item
        ));
        setReviewChapters(nextChapters);
      }

      const refreshedSelected = nextChapters.find((chapter) => Number(chapter.chapter_no) === selectedChapterNo) || nextChapters[0];
      const refreshedRange = refreshedSelected
        ? nextChapters
          .filter((chapter) => Number(chapter.chapter_no || 0) >= selectedChapterNo)
          .sort((a, b) => Number(a.chapter_no || 0) - Number(b.chapter_no || 0))
          .slice(0, targetSize)
        : [];
      const refreshedReview = aggregateReviewPayload(refreshedRange);
      setReviewResult(reviewPayloadFromChapter(refreshedSelected));
      setRepairStates({});
      setRepairError('');
      onStageCompletionChange('review', isReviewStageComplete(nextChapters, publishMinScore));
      await refreshWorkbench(activeDraft.backendId || activeInspirationId);
      setToast(Number.isFinite(refreshedReview?.score)
        ? `正文已修复并重新评分：${refreshedReview?.score} 分`
        : '正文已修复并重新评分');
    } catch (error) {
      const message = error instanceof Error ? error.message : '一键修复失败';
      setRepairStates((items) => Object.fromEntries(Object.keys(items).map((id) => [id, '待修复'])));
      setRepairError(message);
      setToast(message);
    } finally {
      setRepairBusy(false);
      setReviewBusy(false);
      setRepairProgressText('');
    }
  };

  const publishAssistPayload = () => ({
    inspiration_id: activePublishInspirationId || undefined,
    title: publishTitle.trim(),
    description: publishDesc.trim(),
  });

  const generateCover = useCallback(async () => {
    if (!onRequireLogin()) return;
    if (!projectId) {
      setToast('请先选择作品');
      return;
    }
    setCoverGenerating(true);
    setCoverProgress(12);
    setCoverState('AI 正在生成封面...');
    const progressTimer = window.setInterval(() => {
      setCoverProgress((value) => value >= 88
        ? value
        : Math.min(88, value + Math.max(2, Math.round((90 - value) * 0.12))));
    }, 900);
    try {
      const result = await apiRequest<PublishCoverResult>(`/novel_project_app/projects/${projectId}/publish/cover`, {
        method: 'POST',
        body: JSON.stringify({
          inspiration_id: activePublishInspirationId || undefined,
          title: publishTitle.trim(),
          description: publishDesc.trim(),
        }),
      });
      if (!result.cover_url?.trim()) throw new Error('AI封面没有保存成功，本次不会作为有效封面使用');
      setCoverProgress(92);
      setCoverState('封面已生成，正在校验图片资源...');
      await waitForCoverImage(result.cover_url);
      setCoverProgress(98);
      if (coverPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(coverPreview);
      }
      setCoverPreview(result.cover_url);
      setCoverUrl(result.cover_url);
      setCoverProgress(100);
      setCoverState('AI 封面已生成并自动保存');
      await onAssetsRefresh?.();
      setToast('AI封面已生成并自动保存');
    } catch (error) {
      const message = friendlyGenerationError(error, 'AI封面生成失败');
      setCoverState('AI 封面生成失败');
      setCoverProgress(0);
      setToast(message);
    } finally {
      window.clearInterval(progressTimer);
      setCoverGenerating(false);
    }
  }, [
    activePublishInspirationId,
    coverPreview,
    onAssetsRefresh,
    onRequireLogin,
    projectId,
    publishDesc,
    publishTitle,
    setToast,
  ]);

  useEffect(() => {
    if (activeTab !== 'publish' || !projectId || !publishSnapshot || coverUrl || coverGenerating) return;
    const attemptKey = `${projectId}:${activePublishInspirationId || 0}`;
    if (autoCoverAttemptedRef.current.has(attemptKey)) return;
    autoCoverAttemptedRef.current.add(attemptKey);
    void generateCover();
  }, [
    activePublishInspirationId,
    activeTab,
    coverGenerating,
    coverUrl,
    generateCover,
    projectId,
    publishSnapshot,
  ]);

  const generatePublishDescription = async () => {
    if (!onRequireLogin()) return;
    if (!projectId) {
      setToast('请先选择作品');
      return;
    }
    setDescriptionGenerating(true);
    setPublishDesc('');
    setPublishStateText('AI 正在生成作品简介...');
    try {
      const response = await fetch(`${apiBaseUrl}/novel_project_app/projects/${projectId}/publish/description-stream`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(publishAssistPayload()),
      });
      if (!response.ok || !response.body) {
        throw new Error(await streamErrorMessage(response, 'AI简介生成失败'));
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamedText = '';
      let finalDescription = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.trim()) continue;
          const event = parseStreamEvent(line);
          if (event.type === 'chunk') {
            streamedText += event.content || '';
            setPublishDesc(streamedText);
            setPublishStateText('AI 正在生成作品简介...');
          }
          if (event.type === 'retry') {
            streamedText = '';
            setPublishDesc('');
            setPublishStateText(event.message || '网络波动，AI 正在自动重试...');
          }
          if (event.type === 'done') {
            finalDescription = event.description || streamedText;
          }
          if (event.type === 'error') {
            throw new Error(event.message || 'AI简介生成失败');
          }
        }
      }
      if (!finalDescription.trim()) throw new Error('AI简介生成结果为空');
      setPublishDesc(finalDescription);
      setPublishStateText('AI 简介已生成');
      await onAssetsRefresh?.();
      setToast('AI简介已生成');
    } catch (error) {
      const message = friendlyGenerationError(error, 'AI简介生成失败');
      setPublishStateText(message);
      setToast(message);
    } finally {
      setDescriptionGenerating(false);
    }
  };

  const uploadCover = async (file?: File) => {
    if (!file) return;
    if (!onRequireLogin()) return;
    if (!projectId) {
      setToast('请先选择作品');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setToast('请上传图片文件');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setToast('封面图片不能超过 8MB');
      return;
    }
    const previousPreview = coverPreview;
    const previousState = coverState;
    const previewUrl = URL.createObjectURL(file);
    if (coverPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(coverPreview);
    }
    setCoverUploading(true);
    setCoverPreview(previewUrl);
    setCoverState('封面上传中...');
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (activePublishInspirationId) formData.append('inspiration_id', String(activePublishInspirationId));
      const response = await fetch(`${apiBaseUrl}/novel_project_app/projects/${projectId}/publish/cover/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('lingbi-token')}`,
        },
        body: formData,
      });
      const text = await response.text();
      const result = text ? JSON.parse(text) : null;
      if (!response.ok) {
        throw new Error(formatApiError(result) || '封面上传失败');
      }
      if (previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
      setCoverPreview(result.cover_url);
      setCoverUrl(result.cover_url);
      setCoverState(`已上传：${file.name}`);
      setToast('封面已上传并保存');
      await refreshWorkbench(activePublishInspirationId);
      if (projectId) {
        try {
          const snapshot = await apiRequest<WorkbenchSnapshot>(workbenchPath(projectId, activePublishInspirationId || undefined, true));
          setPublishSnapshot(snapshot);
        } catch {
          // 上传已成功，快照刷新失败不影响封面地址保存。
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '封面上传失败';
      if (previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
      setCoverPreview(previousPreview?.startsWith('blob:') ? null : previousPreview);
      setCoverState(previousState || (coverUrl ? 'AI 封面已生成' : 'AI 封面待生成'));
      setToast(message);
    } finally {
      setCoverUploading(false);
    }
  };

  const publishNovel = async () => {
    if (!onRequireLogin()) return;
    if (!projectId) {
      setToast('请先选择作品');
      return;
    }
    if (!publishTitle.trim()) {
      setToast('请先填写书名');
      return;
    }
    if (!publishCategory || !publishCategories.some((item) => item.channel === publishChannel && item.name === publishCategory)) {
      setToast('请先选择后台已启用的作品分类');
      return;
    }
    if (!coverUrl) {
      setToast('请先 AI 生成或上传与作品内容相关的封面');
      return;
    }
    setPublishing(true);
    setPublishStateText('正在发布到书阁...');
    try {
      const result = await apiRequest<PublishResult>(`/novel_project_app/projects/${projectId}/publish`, {
        method: 'POST',
        body: JSON.stringify({
          title: publishTitle.trim(),
          description: publishDesc.trim(),
          cover_url: coverUrl || null,
          inspiration_id: activePublishInspirationId || undefined,
          book_status: publishBookStatus,
          reader: publishChannel,
          category: publishCategory,
        }),
      });
      setPublishStateText('已发布到书阁');
      onStageCompletionChange('publish', true);
      await refreshWorkbench(activePublishInspirationId);
      setToast(result.message || '作品已发布到书阁');
      window.location.hash = `#library/book/${result.book.slug}`;
    } catch (error) {
      const message = error instanceof Error ? error.message : '发布失败';
      setPublishStateText(message);
      setToast(message);
    } finally {
      setPublishing(false);
    }
  };

  const downloadNovel = async () => {
    const snapshot = publishSnapshot;
    const safeTitle = sanitizeFileName(publishTitle || snapshot?.project?.title || 'novel');
    const sourceChapters: BackendChapterSnapshot[] = snapshot?.chapters?.length ? snapshot.chapters : workbenchChapters.map((chapter) => ({
      id: Number(chapter.backendId || chapter.index),
      chapter_no: Number(chapter.index),
      title: chapter.title,
      summary: chapter.summary,
      content: chapter.content,
      words: chapter.words,
    }));
    const chapters = sourceChapters
      .filter((chapter) => normalizeText(chapter.content))
      .sort((a, b) => Number(a.chapter_no || 0) - Number(b.chapter_no || 0));
    if (!chapters.length) {
      setToast('暂无可导出的章节正文');
      return;
    }

    const activeInspiration = snapshot?.inspirations?.find((item) => Number(item.id) === Number(activePublishInspirationId));
    const settingsMap = (snapshot?.settings || []).reduce<Record<string, string>>((acc, item) => {
      acc[item.setting_key] = item.setting_value;
      return acc;
    }, {});
    const chapterTextBlocks = chapters.map((chapter) => {
      const title = normalizeText(chapter.title) || `第${chapter.chapter_no || ''}章`;
      return `${title}\n\n${normalizeText(chapter.content)}`;
    });
    const fullText = [
      `书名：${publishTitle || safeTitle}`,
      `作者：${publishAuthor || '灵笔作者'}`,
      `状态：${publishBookStatus}`,
      '',
      ...chapterTextBlocks,
    ].join('\n\n');
    const markdownText = [
      `# ${publishTitle || safeTitle}`,
      '',
      `> 作者：${publishAuthor || '灵笔作者'}`,
      `> 状态：${publishBookStatus}`,
      `> 简介：${normalizeText(publishDesc) || '暂无简介'}`,
      '',
      ...chapters.flatMap((chapter) => [
        `## ${normalizeText(chapter.title) || `第${chapter.chapter_no || ''}章`}`,
        '',
        normalizeText(chapter.content),
        '',
      ]),
    ].join('\n');
    const platformInfo = [
      `书名：${publishTitle || safeTitle}`,
      `作者：${publishAuthor || '灵笔作者'}`,
      `连载状态：${publishBookStatus}`,
      `作品类型：${settingsMap.genre || snapshot?.project?.genre || '未设置'}`,
      `目标平台：${settingsMap.style_platform || snapshot?.project?.platform || '通用网文平台'}`,
      `预计章节数：${snapshot?.project?.chapter_target || chapters.length}`,
      `单章目标字数：${snapshot?.project?.words_per_chapter || chapterWordLimit}`,
      `已导出章节数：${chapters.length}`,
      `已导出正文总字数：${chapters.reduce((sum, chapter) => sum + Number(chapter.words || normalizeText(chapter.content).length), 0)}`,
      `封面地址：${coverUrl || coverPreview || '未生成或未上传'}`,
      '',
      '作品简介：',
      normalizeText(publishDesc) || '暂无简介',
      '',
      '故事梗概：',
      normalizeText(activeInspiration?.premise) || '暂无梗概',
      '',
      '核心冲突：',
      normalizeText(activeInspiration?.conflict) || '暂无核心冲突',
      '',
      '卖点钩子：',
      normalizeText(activeInspiration?.hook) || '暂无卖点钩子',
      '',
      '投稿使用建议：',
      '1. 多数小说平台后台支持直接粘贴纯文本正文，优先使用“正文-通用纯文本.txt”。',
      '2. 需要保留标题层级或内部归档时，使用“正文-Markdown存档.md”。',
      '3. 平台资料、简介、封面地址和故事卖点集中在本文件，方便上架时复制。',
    ].join('\n');
    const metadata = {
      title: publishTitle || safeTitle,
      author: publishAuthor || '灵笔作者',
      status: publishBookStatus,
      description: normalizeText(publishDesc),
      cover_url: coverUrl || coverPreview || '',
      source_inspiration_id: activePublishInspirationId,
      genre: settingsMap.genre || snapshot?.project?.genre || '',
      platform: settingsMap.style_platform || snapshot?.project?.platform || '',
      chapter_target: snapshot?.project?.chapter_target || chapterTarget,
      words_per_chapter: snapshot?.project?.words_per_chapter || chapterWordLimit,
      exported_at: new Date().toISOString(),
      chapters: chapters.map((chapter) => ({
        chapter_no: chapter.chapter_no,
        title: normalizeText(chapter.title) || `第${chapter.chapter_no || ''}章`,
        words: Number(chapter.words || normalizeText(chapter.content).length),
        summary: normalizeText(chapter.summary),
      })),
      inspiration: activeInspiration || null,
      settings: settingsMap,
    };

    const files: ZipFileEntry[] = [
      {
        name: 'README-使用说明.txt',
        data: [
          '灵笔发布素材包',
          '',
          '这个压缩包用于把作品发布到其他小说平台或做本地归档。',
          '优先使用“正文-通用纯文本.txt”复制正文；需要保留标题结构时使用 Markdown 存档。',
          '平台资料里包含简介、封面地址、故事梗概、核心冲突和卖点，适合上架时复制填写。',
        ].join('\n'),
      },
      { name: '正文-通用纯文本.txt', data: fullText },
      { name: '正文-Markdown存档.md', data: markdownText },
      { name: '平台发布资料.txt', data: platformInfo },
      { name: 'metadata.json', data: JSON.stringify(metadata, null, 2) },
      ...chapters.map((chapter) => ({
        name: `chapters/${String(chapter.chapter_no || 0).padStart(3, '0')}-${sanitizeFileName(normalizeText(chapter.title) || `第${chapter.chapter_no || ''}章`, 'chapter')}.txt`,
        data: `${normalizeText(chapter.title) || `第${chapter.chapter_no || ''}章`}\n\n${normalizeText(chapter.content)}`,
      })),
    ];

    if (coverPreview || coverUrl) {
      try {
        const response = await fetch(coverPreview || coverUrl);
        if (response.ok) {
          const bytes = new Uint8Array(await response.arrayBuffer());
          const type = response.headers.get('content-type') || '';
          const ext = type.includes('png') ? 'png' : type.includes('webp') ? 'webp' : 'jpg';
          files.push({ name: `cover/作品封面.${ext}`, data: bytes, mime: type });
        } else {
          files.push({ name: 'cover/封面地址.txt', data: coverPreview || coverUrl });
        }
      } catch {
        files.push({ name: 'cover/封面地址.txt', data: coverPreview || coverUrl });
      }
    }

    downloadBlob(createZipBlob(files), `${safeTitle}-发布素材包.zip`);
    setToast(`已导出 ${chapters.length} 章发布素材包`);
  };

  const canSaveActiveDraft = hasDraftContent(activeDraft);
  const saveNextLabel = saveBusy
    ? '保存中'
    : isActiveDraftDirty
      ? '保存灵感并进入设定'
      : canSaveActiveDraft
        ? '保存灵感并进入设定'
        : '先填写灵感内容';
  const saveNextClassName = isActiveDraftDirty ? 'synopsis-save-next dirty' : 'synopsis-save-next saved';
  const hasSettingsContent = Boolean(
    selectedGenre.trim()
    || customGenre.trim()
    || styleName.trim()
    || styleNote.trim()
    || selectedRhythms.length
    || boundary.trim(),
  );
  const hasCharactersContent = characterProfiles.length > 0;
  const hasOutlineContent = Boolean(masterOutline.trim() || outlineChapters.length || foreshadowPlans.length);
  const activeStageDataSource = `${projectId || 'empty'}:${activeDraft.backendId || 'empty'}`;
  const outlineDataReady = Boolean(
    stageDataLoaded.outline
    && stageDataSource.outline === activeStageDataSource
    && !stageLoading.outline,
  );
  const showSettingsDecision = activeTab === 'settings'
    && stageDataLoaded.settings
    && !stageLoading.settings
    && !settingsGenerating
    && !hasSettingsContent
    && !manualStages.settings;
  const showCharactersDependency = activeTab === 'characters'
    && stageDataLoaded.characters
    && !stageLoading.characters
    && !charactersGenerating
    && downstreamSavedState.settings === false
    && !hasSettingsContent
    && !hasCharactersContent;
  const showCharactersDecision = activeTab === 'characters'
    && stageDataLoaded.characters
    && !stageLoading.characters
    && !charactersGenerating
    && hasSettingsContent
    && !hasCharactersContent
    && !manualStages.characters;
  const showOutlineDependency = activeTab === 'outline'
    && outlineDataReady
    && !outlineGenerating
    && !chaptersPlanning
    && !hasCharactersContent
    && !hasOutlineContent;
  const showOutlineDecision = activeTab === 'outline'
    && outlineDataReady
    && !outlineGenerating
    && !chaptersPlanning
    && hasCharactersContent
    && !hasOutlineContent
    && !manualStages.outline;

  useEffect(() => {
    if (
      activeTab !== 'outline'
      || !outlineDataReady
      || outlineGenerating
      || chaptersPlanning
    ) return;
    const charactersDone = hasCharactersContent;
    if (stageCompletionRef.current.characters === charactersDone) return;
    stageCompletionRef.current = { ...stageCompletionRef.current, characters: charactersDone };
    onStageCompletionChange('characters', charactersDone);
  }, [
    activeTab,
    chaptersPlanning,
    hasCharactersContent,
    onStageCompletionChange,
    outlineGenerating,
    outlineDataReady,
  ]);

  if (activeTab === 'inspiration') {
    return (
      <main className="wb-stage inspiration-workspace">
        <aside className="idea-rail">
          <div className="idea-rail-head">
            <SectionTitle icon={<FolderOpen size={18} />} title="灵感池" desc={ideaLoading ? '同步灵感池中' : `${inspirations.length} 个灵感`} />
            <button type="button" onClick={createBlankProject} aria-label="新建项目"><Plus size={16} /></button>
          </div>
          <div className="idea-project-list">
            {inspirations.map((item) => (
              <div className={item.id === activeDraft.id ? 'idea-project-item active' : 'idea-project-item'} key={item.id}>
                <button
                  className="idea-open"
                  type="button"
                  onClick={() => {
                    setActiveIdeaId(item.id);
                    setReviewChapters([]);
                    setReviewResult(null);
                    setReviewError('');
                    setGenerationErrors({});
                    const isSaved = Boolean(item.backendId || hasDraftContent(item)) && !dirtyDraftIds[item.id];
                    onStageCompletionChange('inspiration', isSaved);
                    setSaveState(isSaved ? '已保存，可进入设定' : '有未保存修改');
                    if (item.backendId) onInspirationSelect?.(item.backendId, item.title);
                  }}
                >
                  <BookOpen size={15} />
                  <strong>{item.title}</strong>
                  <span>{item.status}</span>
                  <em>{item.updatedAt}</em>
                </button>
                <button className="idea-delete" type="button" onClick={() => setPendingDeleteId(item.id)} aria-label={`删除${item.title}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </aside>

        <section className="idea-work-area">
          <div className="idea-orbit">
            <span><Sparkles size={14} /> 选择一个灵感方向</span>
            <div>
              {ideaPresets.map((preset) => (
                <button type="button" key={preset} onClick={() => setIdeaText(preset)}>
                  {preset}
                </button>
              ))}
            </div>
          </div>

          <form
            className="idea-composer"
            onSubmit={(event) => {
              event.preventDefault();
              generateIdea();
            }}
          >
            <textarea
              value={ideaText}
              onChange={(event) => setIdeaText(event.target.value)}
              placeholder="输入一句话或一段灵感，例如：一个落榜生在旧书摊买到一本会自动续写自己未来三天的小说。"
              disabled={ideaGenerating}
            />
            <div className="idea-actions">
              <span>{ideaGenerating ? '正在为您生成当中' : `${ideaText.trim().length} 字`}</span>
              <button type="submit" disabled={ideaGenerating}><Send size={15} />{ideaGenerating ? '生成中' : '生成新项目梗概'}</button>
            </div>
          </form>

          {ideaGenerating && (
            <div className="idea-generating">
              <Sparkles size={18} />
              <strong>正在拆解灵感、构建冲突和读者钩子</strong>
              <p>{humanizeInspirationStream(streamingText)}</p>
              <span><i /></span>
            </div>
          )}
          {generationErrors.inspiration && !ideaGenerating && (
            <div className="stage-error-card">
              <strong>灵感生成失败</strong>
              <p>{generationErrors.inspiration}</p>
              <button type="button" onClick={generateIdea}>重新生成梗概</button>
            </div>
          )}

          <section className={ideaGenerating ? 'synopsis-board editable generating' : 'synopsis-board editable'}>
            <div className="synopsis-head">
              <SectionTitle icon={<FileText size={18} />} title="故事梗概" desc={saveState} />
            </div>
            <label className={streamingFields.includes('title') ? 'stream-in' : ''}>
              <span>书名</span>
              <input value={activeDraft.title} onChange={(event) => updateActiveDraft({ title: event.target.value })} />
            </label>
            <label className={streamingFields.includes('premise') ? 'stream-in synopsis-premise-field' : 'synopsis-premise-field'}>
              <span>故事梗概 <em>{activeDraft.premise.length}/{SYNOPSIS_LIMIT}</em></span>
              <textarea
                maxLength={SYNOPSIS_LIMIT}
                value={activeDraft.premise}
                onChange={(event) => updateActiveDraft({ premise: event.target.value.slice(0, SYNOPSIS_LIMIT) })}
              />
            </label>
            <label className={streamingFields.includes('stakes') ? 'stream-in synopsis-stakes-field' : 'synopsis-stakes-field'}>
              <span>风险与代价 <em>主角失败或推进剧情时必须承担的后果</em></span>
              <textarea value={activeDraft.stakes} onChange={(event) => updateActiveDraft({ stakes: event.target.value })} />
            </label>
            <div className="synopsis-fields">
              <label className={streamingFields.includes('conflict') ? 'stream-in' : ''}>
                <span>核心冲突</span>
                <textarea rows={2} value={activeDraft.conflict} onChange={(event) => updateActiveDraft({ conflict: event.target.value })} />
              </label>
              <label className={streamingFields.includes('hook') ? 'stream-in' : ''}>
                <span>读者钩子</span>
                <textarea rows={2} value={activeDraft.hook} onChange={(event) => updateActiveDraft({ hook: event.target.value })} />
              </label>
              <label className={streamingFields.includes('boundary') ? 'stream-in' : ''}>
                <span>禁写方向</span>
                <textarea rows={2} value={activeDraft.boundary} onChange={(event) => updateActiveDraft({ boundary: event.target.value })} />
              </label>
            </div>
            <button className={saveNextClassName} type="button" disabled={ideaGenerating || saveBusy || !canSaveActiveDraft} onClick={saveAndOpenSettings}>
              <ArrowRight size={17} />
              {saveNextLabel}
            </button>
          </section>
        </section>
        {pendingDelete && (
          <div className="confirm-backdrop" role="presentation">
            <section className="confirm-dialog" role="dialog" aria-modal="true" aria-label="确认删除灵感">
              <strong>删除这个灵感？</strong>
              <p>{pendingDelete.title} 删除后会从灵感池移除。</p>
              <div>
                <button type="button" onClick={() => setPendingDeleteId(null)}>取消</button>
                <button className="danger" type="button" onClick={confirmDeleteIdea}>确定删除</button>
              </div>
            </section>
          </div>
        )}
      </main>
    );
  }

  if (activeTab === 'settings') {
    return (
      <main className={settingsGenerating ? 'wb-stage settings-workbench generating' : 'wb-stage settings-workbench'}>
        {showSettingsDecision && (
          <StageDecisionCard
            stage="settings"
            primaryLabel="AI生成"
            secondaryLabel="手动填写"
            onPrimary={() => generateSettingsFromAi(activeDraft)}
            onSecondary={startManualSettings}
          />
        )}
        <section className="setting-group">
          <SectionTitle icon={<BookOpen size={18} />} title="题材" desc="题材决定故事规则、读者预期和后续大纲约束。" />
          <div className="genre-options">
            {genreOptions.map((genre) => (
              <button
                className={selectedGenre === genre ? 'active' : ''}
                key={genre}
                type="button"
                onClick={() => {
                  setSelectedGenre(genre);
                  markSettingsDirty();
                }}
              >
                {genre}
              </button>
            ))}
          </div>
          {selectedGenre === '其他' && (
            <input
              className="setting-input"
              value={customGenre}
              onChange={(event) => {
                setCustomGenre(event.target.value);
                markSettingsDirty();
              }}
              placeholder="输入自定义题材"
            />
          )}
        </section>

        <section className="setting-group">
          <SectionTitle icon={<PenLine size={18} />} title="文风" desc="选择目标平台或通用文风，后续章节会按这里的阅读节奏和表达习惯生成。" />
          <div className="style-grid">
            {styleOptions.map(([name, desc]) => (
              <button
                className={selectedStyle === name ? 'active' : ''}
                key={name}
                type="button"
                onClick={() => {
                  setSelectedStyle(name);
                  setStyleName(`${name}文风`);
                  setStyleNote(desc);
                  markSettingsDirty();
                }}
              >
                <strong>{name}</strong>
                <span>{desc}</span>
              </button>
            ))}
          </div>
          <p className="style-note">{styleName || `${selectedStyle}文风`}：{styleNote || 'AI会根据题材自动选择稳定、清晰的叙事风格。'}</p>
        </section>

        <section className="setting-group">
          <SectionTitle icon={<Flag size={18} />} title="节奏规则" desc="勾选后会进入章节生成约束，用来控制每章怎么推进。" />
          <div className="rhythm-grid">
            {rhythmOptions.map(([name, desc]) => {
              const checked = selectedRhythms.includes(name);
              return (
                <button
                  className={checked ? 'active' : ''}
                  key={name}
                  type="button"
                  onClick={() => {
                    setSelectedRhythms((items) =>
                      checked ? items.filter((item) => item !== name) : [...items, name],
                    );
                    markSettingsDirty();
                  }}
                >
                  <strong>{name}</strong>
                  <span>{desc}</span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="setting-group">
          <SectionTitle icon={<BookOpen size={18} />} title="叙事视角" desc="该设定会约束所有章节的人称与观察范围，保存后不可在章节间随意切换。" />
          <div className="genre-options">
            {['第一人称', '第三人称限知', '第三人称全知'].map((perspective) => (
              <button
                className={narrativePerspective === perspective ? 'active' : ''}
                key={perspective}
                type="button"
                onClick={() => {
                  setNarrativePerspective(perspective);
                  markSettingsDirty();
                }}
              >
                {perspective}
              </button>
            ))}
          </div>
        </section>

        <section className="setting-group">
          <SectionTitle icon={<FileText size={18} />} title="篇幅" desc="全书目标章节决定整体节奏，首次生成章节只决定这一次先产出多少章目录。" />
          <div className="number-grid">
            <label>
              <span>全书目标章节</span>
              <input
                min={1}
                max={maxChapters}
                type="number"
                value={chapterTarget}
                onChange={(event) => {
                  const nextTarget = Math.min(Math.max(1, Number(event.target.value)), maxChapters);
                  setChapterTarget(nextTarget);
                  setInitialChapterCount((current) => Math.min(current, nextTarget, 5));
                  markSettingsDirty();
                }}
              />
              <small>整本小说最终总章数，最多 {maxChapters} 章</small>
            </label>
            <label>
              <span>首次生成章节</span>
              <input
                min={1}
                max={Math.min(5, chapterTarget)}
                type="number"
                value={initialChapterCount}
                onChange={(event) => {
                  setInitialChapterCount(Math.min(Math.max(1, Number(event.target.value)), 5, chapterTarget));
                  markSettingsDirty();
                }}
              />
              <small>本次先生成目录，最多 5 章</small>
            </label>
            <label>
              <span>单章字数</span>
              <input
                min={500}
                max={chapterWordLimit}
                step={100}
                type="number"
                value={wordsTarget}
                onChange={(event) => {
                  setWordsTarget(Math.min(Number(event.target.value), chapterWordLimit));
                  markSettingsDirty();
                }}
              />
            </label>
            <label>
              <span>单章上限</span>
              <input
                min={wordsTarget}
                max={10000}
                step={100}
                type="number"
                value={chapterWordLimit}
                onChange={(event) => {
                  const nextLimit = Math.max(Number(event.target.value), wordsTarget);
                  setChapterWordLimit(nextLimit);
                  markSettingsDirty();
                }}
              />
            </label>
            <label>
              <span>预计总字数</span>
              <input readOnly value={totalWordsEstimate.toLocaleString()} />
            </label>
          </div>
        </section>

        <section className="setting-group">
          <SectionTitle icon={<ShieldCheck size={18} />} title="主线边界" desc="真正防跑偏的是边界和禁区，而不是问一堆泛泛的问题。" />
          <textarea
            value={boundary}
            onChange={(event) => {
              setBoundary(event.target.value);
              markSettingsDirty();
            }}
          />
        </section>

        <section className="setting-summary">
          <div className="setting-summary-title">
            <strong>当前设定</strong>
            <small>确认后将以此生成人物</small>
          </div>
          <div className="setting-summary-details">
            <span><em>题材</em>{selectedGenre === '其他' ? customGenre || '其他' : selectedGenre}</span>
            <span><em>文风</em>{styleName || selectedStyle}</span>
            <span><em>视角</em>{narrativePerspective || '未设置'}</span>
            <span><em>节奏</em>{selectedRhythms.length ? selectedRhythms.join('、') : '未选择'}</span>
            <span className="setting-summary-length"><em>篇幅</em>全书 {chapterTarget} 章 · 首次生成 {initialChapterCount} 章 · 每章约 {wordsTarget.toLocaleString()} 字 · 上限 {chapterWordLimit.toLocaleString()} 字 · 总计约 {totalWordsEstimate.toLocaleString()} 字</span>
          </div>
          <button type="button" disabled={settingsGenerating || charactersGenerating} onClick={saveSettingsAndOpenCharacters}>
            {charactersGenerating ? '人物生成中' : '保存设定并进入人物'}
          </button>
        </section>
        {generationErrors.settings && !settingsGenerating && (
          <div className="stage-error-card">
            <strong>设定生成失败</strong>
            <p>{generationErrors.settings}</p>
            <button type="button" onClick={() => generateSettingsFromAi(activeDraft)}>重新生成设定</button>
          </div>
        )}
        {settingsGenerating && (
          <div className="stage-generating-overlay" aria-live="polite">
            <div className="stage-generating-card">
              <Sparkles size={22} />
              <strong>AI正在帮你生成设定</strong>
              <p>{settingGenerationText || '正在分析灵感梗概，准备补全题材、节奏和创作边界。'}</p>
              <span><i /></span>
            </div>
          </div>
        )}
      </main>
    );
  }

  if (activeTab === 'characters') {
    const isGeneratedCharacter = activeCharacter.id.startsWith('char-');
    const filteredCharacterIds = new Set(
      relationFilter ? characterProfiles.filter((item) => item.faction === relationFilter).map((item) => item.id) : [],
    );
    const activeRelationIds = new Set(
      characterRelations
        .filter(([from, to]) => from === activeCharacter.id || to === activeCharacter.id)
        .flatMap(([from, to]) => [from, to]),
    );

    return (
      <main className={charactersGenerating ? 'wb-stage character-workbench generating' : 'wb-stage character-workbench'}>
        {showCharactersDependency && (
          <StageDecisionCard
            stage="characters"
            title="还没有设定，暂时不能生成人物关系"
            body="人物需要依赖题材、文风、篇幅和创作边界。请先返回设定页填写或生成设定。"
            primaryLabel="返回设定"
            onPrimary={onOpenSettings}
          />
        )}
        {showCharactersDecision && (
          <StageDecisionCard
            stage="characters"
            primaryLabel="AI生成"
            secondaryLabel="手动填写"
            onPrimary={generateCharactersFromAi}
            onSecondary={startManualCharacters}
          />
        )}
        <section className="character-editor">
          <div className="character-editor-head">
            <SectionTitle icon={<UserRound size={18} />} title="人物档案" desc="点击右侧人物后，在这里锁定人物写法。" />
            <div className="character-editor-head-actions">
              <span>{characterProfiles.length} 人 · {characterRelations.length} 关系</span>
              {!!characterProfiles.length && (
                <button
                  type="button"
                  disabled={charactersGenerating}
                  onClick={() => {
                    if (!window.confirm('重新生成会覆盖当前人物档案和关系，是否继续？')) return;
                    void generateCharactersFromAi();
                  }}
                >
                  重新生成人物
                </button>
              )}
            </div>
          </div>
          <div className="character-name-row">
            <label>
              <span>姓名</span>
              <input
                value={activeCharacter.name}
                readOnly={isGeneratedCharacter}
                title={isGeneratedCharacter ? 'AI 生成人物名称不可直接修改，请重新生成人物档案。' : undefined}
                onChange={(event) => updateActiveCharacter({ name: event.target.value })}
              />
              {isGeneratedCharacter && <em className="character-name-lock-tip">AI 生成人物名称不可直接修改，避免关系与大纲不同步。</em>}
            </label>
            <label>
              <span>阵营</span>
              <select value={activeCharacter.faction} onChange={(event) => updateActiveCharacter({ faction: event.target.value as CharacterProfile['faction'] })}>
                {factionLabels.map((label) => <option key={label} value={label}>{label}</option>)}
              </select>
            </label>
          </div>
          <label>
            <span>身份 / 定位</span>
            <input value={activeCharacter.identity} onChange={(event) => updateActiveCharacter({ identity: event.target.value })} />
          </label>
          <label>
            <span>核心目标</span>
            <textarea value={activeCharacter.goal} onChange={(event) => updateActiveCharacter({ goal: event.target.value })} />
          </label>
          <label>
            <span>性格特点</span>
            <textarea value={activeCharacter.personality} onChange={(event) => updateActiveCharacter({ personality: event.target.value })} />
          </label>
          <label>
            <span>说话方式</span>
            <textarea value={activeCharacter.speech} onChange={(event) => updateActiveCharacter({ speech: event.target.value })} />
          </label>
          <label>
            <span>秘密 / 反转</span>
            <textarea value={activeCharacter.secret} onChange={(event) => updateActiveCharacter({ secret: event.target.value })} />
          </label>
          <label>
            <span>写作要求</span>
            <textarea value={activeCharacter.requirement} onChange={(event) => updateActiveCharacter({ requirement: event.target.value })} />
          </label>
          <label>
            <span>人物弧光</span>
            <textarea value={activeCharacter.arc} onChange={(event) => updateActiveCharacter({ arc: event.target.value })} />
          </label>
        </section>

        <section className="character-map-panel">
          <div className="character-map-head">
            <span>人物关系图</span>
            <strong>{characterProfiles.length} 人 · {characterRelations.length} 关系 · 点击人物查看</strong>
          </div>
          <div className="character-map-layout">
            <div className="character-graph-plane">
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                {characterRelations.map(([from, to, label, type], index) => {
                  const source = characterProfiles.find((item) => item.id === from);
                  const target = characterProfiles.find((item) => item.id === to);
                  if (!source || !target) return null;
                  const active = from === activeCharacter.id || to === activeCharacter.id;
                  const filtered = !!relationFilter && !filteredCharacterIds.has(from) && !filteredCharacterIds.has(to);
                  const deltaX = target.x - source.x;
                  const deltaY = target.y - source.y;
                  const distance = Math.hypot(deltaX, deltaY) || 1;
                  const offset = 4 + (index % 3) * 2.5;
                  const direction = index % 2 === 0 ? 1 : -1;
                  const labelX = (source.x + target.x) / 2 + (-deltaY / distance) * offset * direction;
                  const labelY = (source.y + target.y) / 2 + (deltaX / distance) * offset * direction;
                  return (
                    <g className={active ? 'active' : ''} key={`${from}-${to}-${label}`}>
                      <line
                        className={[
                          active ? 'active' : '',
                          String(type),
                          filtered ? 'filtered-out' : '',
                        ].filter(Boolean).join(' ')}
                        x1={source.x}
                        y1={source.y}
                        x2={target.x}
                        y2={target.y}
                      />
                      <text x={labelX} y={labelY} textAnchor="middle" dominantBaseline="middle">
                        <title>{label}</title>
                        {compactRelationLabel(label)}
                      </text>
                    </g>
                  );
                })}
              </svg>
              {characterProfiles.map((item) => (
                <button
                  className={[
                    'character-node',
                    item.faction === '主角' ? 'protagonist' : '',
                    item.faction === '敌对' ? 'enemy' : '',
                    item.id === activeCharacter.id ? 'active' : '',
                    activeRelationIds.has(item.id) ? 'related' : '',
                    relationFilter && item.faction !== relationFilter ? 'filtered-out' : '',
                  ].filter(Boolean).join(' ')}
                  key={item.id}
                  style={{ left: `${item.x}%`, top: `${item.y}%` }}
                  type="button"
                  onClick={() => {
                    setActiveCharacterId(item.id);
                    setToast(`已打开 ${item.name} 人物档案`);
                  }}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>
          <div className="character-map-actions">
            <div className="character-legend">
              {factionLabels.map((label) => (
                <button
                  className={relationFilter === label ? 'active' : ''}
                  key={label}
                  type="button"
                  data-type={label}
                  onClick={() => {
                    const next = relationFilter === label ? null : label as CharacterProfile['faction'];
                    setRelationFilter(next);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <button className="stage-primary" type="button" disabled={charactersGenerating || !characterProfiles.length} onClick={saveCharactersAndOpenOutline}>
              {characterProfiles.length ? '保存人物设定并进入大纲' : '请先生成人物'}
            </button>
          </div>
        </section>
        {!characterProfiles.length && !generationErrors.characters && !charactersGenerating && !showCharactersDependency && !showCharactersDecision && (
          <div className="stage-error-card">
            <strong>人物还未生成</strong>
            <p>当前作品还没有人物档案，请先让 AI 生成人物，再进入大纲。</p>
            <button type="button" onClick={generateCharactersFromAi}>开始生成人物</button>
          </div>
        )}
        {generationErrors.characters && !charactersGenerating && (
          <div className="stage-error-card">
            <strong>人物生成失败</strong>
            <p>{generationErrors.characters}</p>
            <button type="button" onClick={generateCharactersFromAi}>重新生成人物</button>
          </div>
        )}
        {charactersGenerating && (
          <div className="stage-generating-overlay" aria-live="polite">
            <div className="stage-generating-card">
              <Sparkles size={22} />
              <strong>AI正在帮你生成人物</strong>
              <p>{characterGenerationText || '正在根据设定补全人物目标、秘密、关系和成长线。'}</p>
              <span><i /></span>
            </div>
          </div>
        )}
      </main>
    );
  }

  if (activeTab === 'outline') {
    return (
      <main className={outlineGenerating || chaptersPlanning ? 'wb-stage outline-workbench generating' : 'wb-stage outline-workbench'}>
        {!outlineDataReady && !outlineGenerating && !chaptersPlanning && (
          <div className="stage-generating-overlay stage-loading-overlay" aria-live="polite" aria-busy="true">
            <div className="stage-generating-card">
              <Loader2 size={30} />
              <strong>正在加载大纲</strong>
              <p>正在同步人物档案和章节目录，加载完成后将自动显示正确内容。</p>
              <span><i /></span>
            </div>
          </div>
        )}
        {showOutlineDependency && (
          <StageDecisionCard
            stage="outline"
            title="当前作品还没有可用的人物档案"
            body="系统已按当前作品重新核对人物数据。大纲需要依赖主要人物、目标、秘密和人物关系，请先返回人物页填写或生成人物档案。"
            primaryLabel="返回人物"
            onPrimary={onOpenCharacters}
          />
        )}
        {showOutlineDecision && (
          <StageDecisionCard
            stage="outline"
            primaryLabel="AI生成"
            secondaryLabel="手动填写"
            onPrimary={generateOutlineFromAi}
            onSecondary={startManualOutline}
          />
        )}
        <section className="outline-editor-panel">
          <SectionTitle icon={<FileText size={18} />} title="总纲" desc="这里锁定全书主线和不跑偏边界。" />
          <label>
            <span>总纲</span>
            <textarea value={masterOutline} onChange={(event) => setMasterOutline(event.target.value)} />
          </label>
          <div className="outline-active-editor">
            <strong>第 {String(activeOutlineChapter.no).padStart(2, '0')} 章章纲</strong>
            <label>
              <span>章节标题</span>
              <input value={activeOutlineChapter.title} onChange={(event) => updateActiveOutlineChapter({ title: event.target.value })} />
            </label>
            <label>
              <span>本章作用</span>
              <textarea value={activeOutlineChapter.purpose} onChange={(event) => updateActiveOutlineChapter({ purpose: event.target.value })} />
            </label>
            <label>
              <span>章节梗概</span>
              <textarea value={activeOutlineChapter.summary} onChange={(event) => updateActiveOutlineChapter({ summary: event.target.value })} />
            </label>
            <div className="outline-mini-grid">
              <label>
                <span>章末钩子</span>
                <input value={activeOutlineChapter.hook} onChange={(event) => updateActiveOutlineChapter({ hook: event.target.value })} />
              </label>
              <label>
                <span>伏笔</span>
                <input value={activeOutlineChapter.foreshadow} onChange={(event) => updateActiveOutlineChapter({ foreshadow: event.target.value })} />
              </label>
            </div>
          </div>
        </section>

        <section className="chapter-catalog-panel">
          <div className="catalog-head">
            <SectionTitle icon={<BookOpen size={18} />} title="最终章节目录" desc="下一阶段会按这个目录逐章写正文。" />
            <div className="catalog-head-actions">
              <AppendChaptersButton
                projectId={projectId}
                inspirationId={activeDraft.backendId}
                currentCount={outlineChapters.length}
                maxChapters={maxChapters}
                disabled={outlineGenerating || chaptersPlanning || !outlineChapters.length}
                onMessage={setToast}
                onComplete={async (result) => {
                  const appended = (result.outlines || []) as BackendOutline[];
                  const nextHooks = (result.hooks || []) as BackendHook[];
                  if (appended.length) {
                    setOutlineChapters((current) => {
                      const byNo = new Map(current.map((item) => [item.no, item]));
                      appended.forEach((item) => {
                        const chapter = toOutlineChapter(item);
                        byNo.set(chapter.no, chapter);
                      });
                      return Array.from(byNo.values()).sort((a, b) => a.no - b.no);
                    });
                    setActiveOutlineId(toOutlineChapter(appended[0]).id);
                  }
                  if (nextHooks.length) {
                    setForeshadowPlans((current) => {
                      const next = [...current];
                      nextHooks.forEach((item) => {
                        const converted = toForeshadowPlan(item, next.length);
                        const index = next.findIndex((existing) => existing.name === converted.name);
                        if (index >= 0) next[index] = converted;
                        else next.push(converted);
                      });
                      return next;
                    });
                  }
                  await refreshWorkbench(activeDraft.backendId);
                }}
              />
              <button type="button" disabled={outlineGenerating || chaptersPlanning} onClick={generateOutlineFromAi}>重新生成目录</button>
            </div>
          </div>
          <div className="chapter-catalog-list">
            {outlineChapters.map((chapter) => (
              <button
                className={chapter.id === activeOutlineChapter.id ? 'active' : ''}
                key={chapter.id}
                type="button"
                onClick={() => setActiveOutlineId(chapter.id)}
              >
                <span>{String(chapter.no).padStart(2, '0')}</span>
                <strong>{chapter.title}</strong>
                <p>{chapter.summary}</p>
              </button>
            ))}
          </div>
        </section>

        <section className="foreshadow-ledger-panel">
          <div className="catalog-head">
            <SectionTitle icon={<Flag size={18} />} title="伏笔账本" desc="伏笔必须绑定埋设章和回收章，防止后文忘线。" />
          </div>
          <div className="foreshadow-ledger-list">
            {foreshadowPlans.map((item, index) => (
              <div className="foreshadow-ledger-row" key={item.id}>
                <span className="foreshadow-index">{String(index + 1).padStart(2, '0')}</span>
                <input value={item.name} onChange={(event) => updateForeshadowPlan(item.id, { name: event.target.value })} />
                <select value={item.plantChapter} onChange={(event) => updateForeshadowPlan(item.id, { plantChapter: Number(event.target.value) })}>
                  {outlineChapters.map((chapter) => <option key={chapter.id} value={chapter.no}>第 {String(chapter.no).padStart(2, '0')} 章</option>)}
                </select>
                <select value={item.revealChapter} onChange={(event) => updateForeshadowPlan(item.id, { revealChapter: Number(event.target.value) })}>
                  {outlineChapters.map((chapter) => <option key={chapter.id} value={chapter.no}>第 {String(chapter.no).padStart(2, '0')} 章</option>)}
                </select>
                <select value={item.state} onChange={(event) => updateForeshadowPlan(item.id, { state: event.target.value })}>
                  <option>已规划</option>
                  <option>待强化</option>
                  <option>待确认</option>
                  <option>已锁定</option>
                </select>
                <textarea value={item.rule} onChange={(event) => updateForeshadowPlan(item.id, { rule: event.target.value })} />
              </div>
            ))}
          </div>
          <button className="stage-primary" type="button" disabled={outlineGenerating || chaptersPlanning || !outlineChapters.length} onClick={saveOutlineAndOpenChapters}>
            {chaptersPlanning ? '章节计划生成中' : '保存大纲并进入章节'}
          </button>
        </section>
        {(generationErrors.outline || generationErrors.chapters) && !outlineGenerating && !chaptersPlanning && (
          <div className="stage-error-card">
            <strong>{generationErrors.chapters ? '章节计划生成失败' : '大纲生成失败'}</strong>
            <p>{generationErrors.chapters || generationErrors.outline}</p>
            <button type="button" onClick={generationErrors.chapters ? planChaptersFromOutline : generateOutlineFromAi}>
              {generationErrors.chapters ? '重新生成章节计划' : '重新生成大纲'}
            </button>
          </div>
        )}
        {(outlineGenerating || chaptersPlanning) && (
          <div className="stage-generating-overlay" aria-live="polite">
            <div className="stage-generating-card">
              <Sparkles size={22} />
              <strong>{chaptersPlanning ? 'AI正在帮你生成章节计划' : 'AI正在帮你生成大纲'}</strong>
              <p>{chaptersPlanning ? chaptersPlanningText || '正在把章节目录写入章节编辑器。' : outlineGenerationText || '正在根据人物关系和设定规划总纲、章节和伏笔。'}</p>
              <span><i /></span>
            </div>
          </div>
        )}
      </main>
    );
  }

  if (activeTab === 'review') {
    const targetSize = reviewTargetSize(reviewMode);
    const selectedChapterNo = Number(selectedReviewChapter?.chapter_no || reviewChapterNo);
    const reviewTargetChapters = selectedReviewChapter
      ? reviewChapters
        .filter((chapter) => Number(chapter.chapter_no || 0) >= selectedChapterNo)
        .sort((a, b) => Number(a.chapter_no || 0) - Number(b.chapter_no || 0))
        .slice(0, targetSize)
      : [];
    const missingContentChapters = reviewTargetChapters.filter((chapter) => !hasReviewChapterContent(chapter));
    const hasIncompleteTarget = Boolean(selectedReviewChapter) && reviewTargetChapters.length < targetSize;
    const hasMissingContent = !selectedReviewChapter || hasIncompleteTarget || missingContentChapters.length > 0;
    const missingReviewChapters = reviewTargetChapters.filter((chapter) => hasReviewChapterContent(chapter) && !hasStoredReview(chapter));
    const hasMissingReview = !hasMissingContent && missingReviewChapters.length > 0;
    const activeReviewPayload = (!hasMissingContent && !hasMissingReview)
      ? aggregateReviewPayload(reviewTargetChapters)
      : null;
    const reviewScore = activeReviewPayload?.score ?? Number.NaN;
    const selectedOutlineChapter = outlineChapters.find((item) => Number(item.no) === Number(reviewChapterNo));
    const reviewDimensions = normalizeReviewDimensions(activeReviewPayload, Number.isFinite(reviewScore) ? Number(reviewScore) : 88, publishMinScore);
    const reviewIssueCards = (activeReviewPayload?.problems || []).filter((item) => item.message).slice(0, 4);
    const selectedHasContent = hasReviewChapterContent(selectedReviewChapter);
    const reviewRangePassed = Boolean(
      activeReviewPayload
      && Number.isFinite(reviewScore)
      && Number(reviewScore) >= publishMinScore
      && reviewDimensions.every((item) => item.passed)
      && reviewIssueCards.length === 0
    );
    const reviewRangeLabel = reviewTargetChapters.length > 1
      ? `第 ${String(reviewTargetChapters[0]?.chapter_no || selectedChapterNo).padStart(2, '0')} - ${String(reviewTargetChapters[reviewTargetChapters.length - 1]?.chapter_no || selectedChapterNo).padStart(2, '0')} 章`
      : `第 ${String(selectedChapterNo || 1).padStart(2, '0')} 章`;
    const missingContentLabel = hasIncompleteTarget
      ? `${reviewRangeLabel}范围内章节计划不完整`
      : missingContentChapters.map((chapter) => `第 ${String(chapter.chapter_no || '').padStart(2, '0')} 章`).join('、');
    const missingReviewLabel = missingReviewChapters.map((chapter) => `第 ${String(chapter.chapter_no || '').padStart(2, '0')} 章`).join('、');
    const reviewStateLabel = repairBusy
      ? '修复中'
      : reviewBusy
        ? '审稿中'
        : hasMissingContent
          ? '缺正文'
          : hasMissingReview
            ? '待审稿'
            : reviewRangePassed ? '通过' : '待修';
    const emptyStateClass = reviewBusy
      ? 'review-empty-state loading'
      : activeReviewPayload && reviewRangePassed
        ? 'review-empty-state passed'
        : 'review-empty-state pending';
    const emptyStateTitle = reviewBusy
      ? 'AI正在读取章节正文'
      : hasMissingContent
        ? '当前审稿范围没有完整正文'
        : hasMissingReview
          ? '当前章节尚未审稿'
          : activeReviewPayload && reviewRangePassed
            ? '没有需要修复的问题'
            : activeReviewPayload ? '分项判定已完成' : '还没有进行审稿';
    const emptyStateBody = reviewBusy
      ? '正在按四个固定分项判定，完成后这里只保留必须修复的问题。'
      : hasMissingContent
        ? `${missingContentLabel || reviewRangeLabel}还没有正文，无法生成真实评分。请先回到章节页生成正文，再重新审稿。`
        : hasMissingReview
          ? `${missingReviewLabel || reviewRangeLabel}已有正文但还没有真实审稿结果，请先点击左侧“开始审稿”。`
          : activeReviewPayload && reviewRangePassed
            ? '恭喜，当前审稿范围没有必须修复的问题，可以正常进入发布流程。'
            : activeReviewPayload ? '当前存在未达标分项，建议修订后重新审稿。' : '请先点击左侧的“开始审稿”，生成真实审稿结果后，这里会列出需要修复的清单。';
    const baselineTitle = selectedOutlineChapter?.title
      || (selectedReviewChapter ? normalizeChapterTitle(selectedReviewChapter.title || '', Number(selectedReviewChapter.chapter_no || 1), selectedReviewChapter.summary || '') : '等待选择章节');
    const baselineGoal = selectedOutlineChapter?.purpose || selectedReviewChapter?.summary || '按当前大纲、人物和伏笔进行一致性审稿';
    const baselineHook = selectedOutlineChapter?.hook || selectedOutlineChapter?.foreshadow || '对照本章目标、章末钩子和伏笔账本';
    return (
      <main className="wb-stage review-workbench">
        <section className="review-control-panel">
          <SectionTitle icon={<PenLine size={18} />} title="审稿范围" desc="按章节正文对照创作基准审稿。" />
          <div className="review-form-grid">
            <label>
              <span>审稿章节</span>
              <select
                value={reviewChapterNo}
                onChange={(event) => {
                  const nextNo = Number(event.target.value);
                  setReviewChapterNo(nextNo);
                  const nextChapter = reviewChapters.find((chapter) => Number(chapter.chapter_no) === nextNo);
                  setReviewResult(reviewPayloadFromChapter(nextChapter));
                  setReviewError('');
                  setRepairError('');
                }}
              >
                {reviewChapters.length ? reviewChapters.map((chapter) => (
                  <option key={chapter.id} value={chapter.chapter_no}>
                    第 {String(chapter.chapter_no || 1).padStart(2, '0')} 章 · {normalizeChapterTitle(chapter.title || '', Number(chapter.chapter_no || 1), chapter.summary || '')}
                  </option>
                )) : <option value={reviewChapterNo}>暂无可审章节</option>}
              </select>
            </label>
            <label>
              <span>审稿方式</span>
              <select value={reviewMode} onChange={(event) => setReviewMode(event.target.value)}>
                <option>单章精审</option>
                <option>连续三章</option>
                <option>向后抽检</option>
              </select>
            </label>
          </div>
          <div className="review-baseline">
            <strong>本章基准</strong>
            <div className="review-baseline-grid">
              <span>01 · {baselineTitle}</span>
              <span>02 · {baselineGoal}</span>
              <span>03 · 正文 {Number(selectedReviewChapter?.words || 0).toLocaleString()} 字</span>
              <span>04 · {reviewMode === '单章精审' ? baselineHook : `${reviewMode}范围：${reviewRangeLabel}`}</span>
            </div>
          </div>
          {reviewError && (
            <div className="stage-error-card">
              <strong>审稿失败</strong>
              <p>{reviewError}</p>
              <button type="button" onClick={runChapterReview}>重新审稿</button>
            </div>
          )}
          <button className="stage-primary" type="button" disabled={repairBusy || reviewBusy || !selectedReviewChapter?.id} onClick={runChapterReview}>
            {repairBusy ? '修复后重新评分中' : reviewBusy ? 'AI正在审稿' : !selectedHasContent ? '先生成正文' : hasStoredReview(selectedReviewChapter) ? '重新审稿' : '开始审稿'}
          </button>
        </section>

        <section className="review-result-panel">
          <div className="review-result-head">
            <SectionTitle icon={<ShieldCheck size={18} />} title="分项判定" desc="审稿结果用于辅助优化，不影响进入发布。" />
            <div className="score-number stage-score" data-state={reviewStateLabel}>
              <strong>{Number.isFinite(reviewScore) ? reviewScore : '-'}</strong>
              <span>/ 100</span>
              <b>{reviewStateLabel}</b>
            </div>
          </div>
          <div className="review-dimension-grid">
            {reviewDimensions.map((item) => (
              <button className="review-dimension-card" data-state={item.status === '待审' ? '待审' : item.passed ? '通过' : '待修'} key={item.name} type="button" onClick={() => setToast(item.suggestion || item.comment)}>
                <div>
                  <strong>{item.name}</strong>
                  <span>{item.status}</span>
                </div>
                <em>{Number.isFinite(item.score) ? item.score : '-'}</em>
                <p>{item.comment}</p>
                <small>{item.status === '待审' ? '审稿后显示真实分数' : Number(item.score) >= publishMinScore ? `${publishMinScore}分以上，达到发布线` : item.suggestion || `低于${publishMinScore}分，需要修复后重审`}</small>
              </button>
            ))}
          </div>
          <button className="stage-primary" type="button" onClick={() => { onOpenPublish(); setToast('进入发布'); }}>我要发布</button>
        </section>

        <section className="review-issue-panel">
          <SectionTitle icon={<Flag size={18} />} title="问题修复清单" desc="每条问题必须能定位到章节和修订动作。" />
          <div className="review-issue-list">
            {reviewIssueCards.length ? reviewIssueCards.map((item, index) => {
              const issueId = `review-${index}`;
              const repairState = repairStates[issueId] || '待修复';
              return (
              <button className="review-issue-card" data-level={item.level || 'suggestion'} key={issueId} type="button" onClick={() => setToast(item.location || `第 ${reviewChapterNo} 章`)}>
                <div className="review-issue-top">
                  <span className="issue-level">{item.level || '建议'}</span>
                  <i className="repair-state" data-state={repairState}>
                    {repairState === '已修复'
                      ? <><CheckCircle size={14} />修复完成</>
                      : repairState === '修复中'
                        ? <><Loader2 size={14} />修复中</>
                        : repairState}
                  </i>
                </div>
                <strong>{item.message || '优化建议'}</strong>
                <em>{item.location || `第 ${String(reviewChapterNo).padStart(2, '0')} 章`}</em>
                <p>{item.suggestion || item.message || '可回到章节页按建议修订正文。'}</p>
                <small>建议先修复高风险项，再重新审稿。</small>
              </button>
              );
            }) : (
              <div className={emptyStateClass}>
                <div className="review-empty-icon">
                  {reviewBusy ? <Loader2 size={24} /> : activeReviewPayload && reviewRangePassed ? <CheckCircle size={26} /> : <Flag size={25} />}
                </div>
                <strong>{emptyStateTitle}</strong>
                <p>{emptyStateBody}</p>
                <div className="review-empty-tags">
                  {reviewBusy ? (
                    <>
                      <span>读取正文</span>
                      <span>判定分项</span>
                      <span>生成清单</span>
                    </>
                  ) : activeReviewPayload && reviewRangePassed ? (
                    <>
                      <span>一致性通过</span>
                      <span>节奏通过</span>
                      <span>可以发布</span>
                    </>
                  ) : hasMissingContent ? (
                    <>
                      <span>缺少正文</span>
                      <span>分数为空</span>
                      <span>仍可发布</span>
                    </>
                  ) : hasMissingReview ? (
                    <>
                      <span>等待审稿</span>
                      <span>分项为空</span>
                      <span>仍可发布</span>
                    </>
                  ) : (
                    <>
                      <span>等待审稿</span>
                      <span>未生成清单</span>
                      <span>先开始审稿</span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          {repairError && (
            <div className="stage-error-card">
              <strong>{repairBusy ? '自动修复处理中' : '自动处理未完成'}</strong>
              <p>{repairError}</p>
            </div>
          )}
          <button className="stage-primary" type="button" disabled={repairBusy || reviewBusy || !reviewIssueCards.length} onClick={runAutoRepair}>
            {repairBusy && <Loader2 size={16} />}
            {repairBusy ? repairProgressText || '正在修复并重新评分' : '一键修复'}
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="wb-stage publish-workbench">
      <section className="publish-shell">
        <div className="publish-header">
          <SectionTitle icon={<UploadCloud size={18} />} title="发布作品" desc="整理封面、书籍信息，确认后发布到书阁。" />
          <span>{publishStateText || `审稿分 ${score} / 100`}</span>
        </div>

        <div className="publish-main-panel">
          <section className="publish-cover-panel">
            <div className="publish-cover-toolbar">
              <SectionTitle icon={<ImageIcon size={18} />} title="作品封面" />
              <em>{coverState}</em>
            </div>
            <div className={coverGenerating ? 'publish-cover-preview is-generating' : 'publish-cover-preview'}>
              {coverPreview ? (
                <img
                  src={coverPreview}
                  alt="小说封面"
                  decoding="async"
                  onError={() => {
                    setCoverPreview(null);
                    setCoverState('封面已自动保存，但图片暂时加载失败，请稍后刷新或手动重新生成');
                  }}
                />
              ) : (
                <div>
                  <span>灵笔出品</span>
                  <strong>{publishTitle}</strong>
                  <em>{publishAuthor}</em>
                </div>
              )}
              {coverGenerating && (
                <div className="publish-cover-generating-mask" aria-live="polite">
                  <Sparkles size={24} />
                  <strong>AI 正在生成封面</strong>
                  <p>{coverProgress < 35 ? '正在整理作品梗概和小说封面提示词' : coverProgress < 75 ? '正在调用首选模型绘制小说封面' : coverProgress < 90 ? '正在等待图片生成完成' : '正在保存并校验封面资源'}</p>
                  <span>{coverProgress}%</span>
                  <i><b style={{ width: `${coverProgress}%` }} /></i>
                </div>
              )}
            </div>
            <div className="publish-cover-actions">
              <button className={coverGenerating ? 'is-generating' : ''} type="button" disabled={coverGenerating || coverUploading} onClick={generateCover}>
                <Sparkles size={15} />{coverGenerating ? '生成中' : 'AI 生成封面'}
              </button>
              <label className={coverUploading ? 'is-generating' : ''}>
                {coverUploading ? <Loader2 size={15} /> : <UploadCloud size={15} />}
                {coverUploading ? '上传中' : '上传封面'}
                <input
                  type="file"
                  accept="image/*"
                  disabled={coverGenerating || coverUploading}
                  onChange={(event) => {
                    void uploadCover(event.target.files?.[0]);
                    event.target.value = '';
                  }}
                />
              </label>
            </div>
          </section>

          <section className="publish-info-panel">
            <SectionTitle icon={<BookOpen size={18} />} title="上架信息" />
            <label>
              <span>书名</span>
              <input value={publishTitle} onChange={(event) => setPublishTitle(event.target.value)} />
            </label>
            <label>
              <span>作者（当前用户昵称，如需修改请前往个人设置修改昵称）</span>
              <input value={publishAuthor} readOnly title="作者署名使用当前用户昵称" />
            </label>
            <div className="publish-field-group">
              <div className="publish-label-row">
                <span>作品简介</span>
                <button className={descriptionGenerating ? 'is-generating' : ''} type="button" disabled={descriptionGenerating} onClick={generatePublishDescription}>
                  {descriptionGenerating ? <Loader2 size={13} /> : <Sparkles size={13} />}
                  {descriptionGenerating ? 'AI生成中' : 'AI生成'}
                </button>
              </div>
              <textarea className={descriptionGenerating ? 'is-streaming' : ''} value={publishDesc} onChange={(event) => setPublishDesc(event.target.value)} />
            </div>
            <label>
              <span>作品分类（必选，发布后用于分类榜单）</span>
              <div className="publish-category-row">
                <div className="publish-status-options">
                  {(['男频', '女频'] as const).map((item) => (
                    <button className={publishChannel === item ? 'active' : ''} key={item} type="button" onClick={() => setPublishChannel(item)}>{item}</button>
                  ))}
                </div>
                <select value={publishCategory} disabled={!publishCategories.some((item) => item.channel === publishChannel)} onChange={(event) => setPublishCategory(event.target.value)}>
                  {publishCategories.filter((item) => item.channel === publishChannel).map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
                </select>
              </div>
            </label>
            <label>
              <span>发布状态</span>
              <div className="publish-status-options">
                {(['连载中', '已完结'] as const).map((item) => (
                  <button
                    className={publishBookStatus === item ? 'active' : ''}
                    key={item}
                    type="button"
                    onClick={() => setPublishBookStatus(item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </label>
          </section>
        </div>

        <div className="publish-action-panel">
          <button className="stage-primary" type="button" disabled={publishing || !publishCategory} onClick={publishNovel}><Send size={16} />{publishing ? '发布中' : '发布书阁'}</button>
          <button className="stage-primary ghost" type="button" onClick={downloadNovel}><Download size={16} />下载发布包</button>
        </div>
      </section>
    </main>
  );
};

export default StagePanels;
